<template>
    <div class="page-shell page">
        <div class="page-header">
            <div class="list-toolbar">
                <div class="toolbar-left">
                    <el-select
                        v-if="accounts.length > 1"
                        v-model="accountId"
                        filterable
                        placeholder="账号"
                        style="width: 200px"
                        :disabled="loading"
                        @change="onAccountChange"
                    >
                        <el-option
                            v-for="acc in accounts"
                            :key="acc.id"
                            :label="acc.label"
                            :value="acc.id"
                        />
                    </el-select>
                    <el-select
                        v-model="appId"
                        filterable
                        clearable
                        placeholder="应用切换"
                        style="width: 360px"
                        :disabled="loading || !accountId"
                        @change="onAppChange"
                    >
                        <el-option
                            v-for="app in apps"
                            :key="app.appId"
                            :label="app.label"
                            :value="app.appId"
                        />
                    </el-select>
                    <div class="filter-group">
                        <el-select
                            v-model="statusFilter"
                            placeholder="中介组状态"
                            style="width: 140px"
                            :disabled="loading"
                        >
                            <el-option label="启用" value="ENABLED" />
                            <el-option label="停用" value="DISABLED" />
                            <el-option label="全部" value="ALL" />
                        </el-select>
                        <el-input
                            v-model="nameQuery"
                            clearable
                            placeholder="名称搜索"
                            style="width: 200px"
                            :prefix-icon="Search"
                        />
                    </div>
                </div>
                <div class="toolbar-right">
                    <el-button :disabled="loading || !accountId" @click="reloadList">刷新</el-button>
                    <el-button
                        type="primary"
                        :disabled="loading || !accounts.length"
                        @click="openCreateGroup"
                    >
                        新建中介组
                    </el-button>
                </div>
            </div>
            <div v-if="listHint" class="hint-line">{{ listHint }}</div>
        </div>

        <div class="page-body page-body--table-sticky">
            <el-table :data="filteredGroups" v-loading="loading" stripe border :height="table_area_height">
                <el-table-column label="名称" min-width="200">
                    <template #default="{row}">{{ row.displayName || row.mediationGroupId }}</template>
                </el-table-column>
                <el-table-column label="状态" width="90">
                    <template #default="{row}">
                        <el-tag :type="row.state === 'ENABLED' ? 'success' : 'info'" size="small">
                            {{ row.state === 'ENABLED' ? '启用' : (row.state === 'DISABLED' ? '停用' : (row.state || '-')) }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="收入" width="100">
                    <template #default="{row}">{{ formatMoney(row.revenue) }}</template>
                </el-table-column>
                <el-table-column label="展示次数" width="110">
                    <template #default="{row}">{{ formatCount(row.impressions) }}</template>
                </el-table-column>
                <el-table-column label="操作" width="180">
                    <template #default="{row}">
                        <el-button link type="primary" @click="openEditor(row)">编辑瀑布流（OPMC）</el-button>
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <el-dialog
            v-model="editorOpen"
            class="waterfall-editor-dialog"
            width="90%"
            :show-close="true"
            destroy-on-close
            @closed="resetEditor"
        >
            <div v-loading="editorLoading" class="editor-body">
                <div class="editor-header-block">
                    <div class="group-title">中介组名称：{{ editingGroup?.displayName || editingGroup?.mediationGroupId }}</div>
                    <div class="header-actions">
                        <div class="ad-unit-line">
                            <span class="label">广告单元：</span>
                            <span class="value">{{ standardAdUnitLabel }}</span>
                            <el-button
                                size="small"
                                :disabled="editorWriteLocked"
                                @click="targetingEditorOpen = true"
                            >修改广告单元</el-button>
                        </div>
                        <div class="ad-source-dropdowns" style="display:flex; gap:12px; flex-wrap:wrap; margin-top:8px; align-items:center">
                            <span class="label">第三方平台</span>
                            <el-select
                                v-model="selectedPlatformIds"
                                multiple
                                filterable
                                clearable
                                placeholder="筛选第三方平台（默认全选，可去掉）"
                                style="min-width: 320px; flex: 1"
                                :disabled="editorWriteLocked"
                                @change="onPlatformFilterChange"
                            >
                                <el-option
                                    v-for="wf in waterfallPanelOptions"
                                    :key="wf.id"
                                    :label="`${wf.name}（${wf.lineCount}）`"
                                    :value="wf.id"
                                />
                            </el-select>
                        </div>
                    </div>
                    <div class="hint-line">
                        多选平台后混合展示分层，按底价从高到低排序。新建层只能创建对方广告单元，不能选择已有。
                        格式锁定 X={{ formatX || '未确定' }}（由普通广告单元决定）。
                        <template v-if="!waterfallPanelOptions.length">
                            当前账号无已支持的瀑布流广告源。
                        </template>
                    </div>
                    <div v-if="editorWriteLocked" class="hint-line" style="color:#b88230">
                        当前中介组有运行中的 AB 测试，瀑布流已锁定。请先停止 AB 测试后再修改。
                    </div>
                </div>

                <div class="editor-section section-waterfall">
                    <div class="section-label" style="display:flex; align-items:center; gap:8px; flex-wrap:wrap">
                        <span>{{ editorWriteLocked ? 'AB 测试对照' : '混合瀑布流' }}</span>
                        <el-tag size="small" effect="dark">Y = {{ formatX || '?' }}</el-tag>
                        <div style="margin-left:auto; display:flex; gap:8px">
                            <el-button
                                v-if="!editorWriteLocked"
                                size="small"
                                type="warning"
                                plain
                                :disabled="!editingGroup"
                                @click="openCreateAbDialog"
                            >创建 AB 测试</el-button>
                            <el-button
                                v-else
                                size="small"
                                type="danger"
                                plain
                                :disabled="!editingGroup"
                                @click="openStopAbDialog"
                            >停止 AB 测试</el-button>
                            <el-button size="small" type="primary" plain :disabled="!editingGroup || editorWriteLocked" @click="openImportDialog">导入</el-button>
                        </div>
                    </div>
                    <div class="hint-line" v-if="!editorWriteLocked">
                        每行选择所属平台；Apply 时按该行平台创建对方单元并 mapping。已有行可改底价。
                        导入会清除未提交的新建草稿，并将原有层标记删除（置灰沉底）。
                        删除：新建行直接移掉；已有行置灰（可撤销），确认时再计划移除。
                    </div>
                    <div class="hint-line" v-else>
                        左侧为对照 A，右侧为实验组 B。同名/同底价属于正常现象；停止实验时选择保留一侧即可。
                    </div>

                    <template v-if="editorWriteLocked">
                        <div class="ab-split">
                            <div class="ab-panel">
                                <div class="section-label">对照 A</div>
                                <div
                                    v-for="row in abVariantARows"
                                    :key="'a-' + row.key"
                                    class="waterfall-row ab-readonly-row"
                                >
                                    <el-tag size="small" type="info" effect="plain">{{ formatX || '?' }}</el-tag>
                                    <el-tag size="small" type="success" effect="plain">{{ rowSourceTag(row) }}</el-tag>
                                    <el-tag
                                        v-if="String(row.cpmMode || '').toUpperCase() === 'LIVE'"
                                        size="small"
                                        type="warning"
                                        effect="plain"
                                    >LIVE</el-tag>
                                    <span class="ab-cell">底价 {{ row.floorPrice != null ? Number(row.floorPrice).toFixed(2) : '—' }}</span>
                                    <span class="ab-cell">{{ row.displayName || '—' }}</span>
                                    <span class="ab-cell ab-muted">{{ row.adUnitId || '—' }}</span>
                                </div>
                                <div v-if="!abVariantARows.length" class="hint-line">无对照线路</div>
                            </div>
                            <div class="ab-panel">
                                <div class="section-label">实验组 B</div>
                                <div
                                    v-for="row in abVariantBRows"
                                    :key="'b-' + row.key"
                                    class="waterfall-row ab-readonly-row"
                                >
                                    <el-tag size="small" type="info" effect="plain">{{ formatX || '?' }}</el-tag>
                                    <el-tag size="small" type="success" effect="plain">{{ rowSourceTag(row) }}</el-tag>
                                    <el-tag
                                        v-if="String(row.cpmMode || '').toUpperCase() === 'LIVE'"
                                        size="small"
                                        type="warning"
                                        effect="plain"
                                    >LIVE</el-tag>
                                    <span class="ab-cell">底价 {{ row.floorPrice != null ? Number(row.floorPrice).toFixed(2) : '—' }}</span>
                                    <span class="ab-cell">{{ row.displayName || '—' }}</span>
                                    <span class="ab-cell ab-muted">{{ row.adUnitId || '—' }}</span>
                                </div>
                                <div v-if="!abVariantBRows.length" class="hint-line">无实验组线路</div>
                            </div>
                        </div>
                    </template>

                    <template v-else>
                    <div
                        v-if="selectedWaterfallId === APPLOVIN_SOURCE_ID"
                        class="partner-toolbar"
                        style="display:flex; gap:12px; flex-wrap:wrap; margin-bottom:10px; align-items:center"
                    >
                        <span class="label">AppLovin SDK Key</span>
                        <el-input
                            v-model="applovinSdkKey"
                            placeholder="必填 · 存 localStorage"
                            style="width: 320px"
                            @change="persistApplovinSdkKey"
                        />
                    </div>
                    <div
                        v-if="selectedWaterfallId === MINTEGRAL_SOURCE_ID"
                        class="partner-toolbar"
                        style="display:flex; gap:12px; flex-wrap:wrap; margin-bottom:10px; align-items:center"
                    >
                        <span class="label">Mintegral Placement</span>
                        <el-select
                            v-model="mintegralPlacementId"
                            filterable
                            clearable
                            placeholder="选择 Placement（新建 Unit 所需）"
                            style="min-width: 260px"
                            @change="onMintegralPlacementChange"
                        >
                            <el-option
                                v-for="pl in mintegralPlacements"
                                :key="pl.id"
                                :label="pl.label"
                                :value="pl.id"
                            />
                        </el-select>
                        <el-tag v-if="mintegralAppId" size="small" effect="plain">
                            App {{ mintegralAppId }}
                        </el-tag>
                    </div>

                    <div v-if="!selectedPlatformIds.length" class="hint-line">请选择平台</div>

                    <div
                        v-for="(row, index) in visibleDraftRows"
                        :key="row.key"
                        class="waterfall-row"
                        :class="{'is-removed': row.deleted}"
                    >
                        <el-button
                            circle
                            size="small"
                            :disabled="row.deleted || !formatX || editorWriteLocked"
                            @click="addRowAfter(index)"
                        >+</el-button>
                        <el-tag size="small" type="info" effect="plain" class="format-lock">{{ formatX || '?' }}</el-tag>
                        <el-tag v-if="row.deleted" size="small" type="info">已移除（仅预览）</el-tag>
                        <el-select
                            v-if="!row.deleted && rowMode(row) === 'new'"
                            :model-value="row.adSourceId || row.waterfallId"
                            filterable
                            placeholder="选择平台"
                            style="width: 180px"
                            :disabled="editorWriteLocked"
                            @change="onRowPlatformChange(row, $event)"
                        >
                            <el-option
                                v-for="wf in waterfallPanelOptions"
                                :key="wf.id"
                                :label="wf.name"
                                :value="wf.id"
                            />
                        </el-select>
                        <el-tag
                            v-else-if="!row.deleted"
                            size="small"
                            type="success"
                            effect="plain"
                        >{{ rowSourceTag(row) }}</el-tag>
                        <el-tag v-if="!row.deleted && String(row.cpmMode || '').toUpperCase() === 'LIVE'" size="small" type="warning" effect="plain">LIVE</el-tag>
                        <el-tag v-if="!row.deleted && rowMode(row) === 'new'" size="small" type="success" effect="plain">新建</el-tag>
                        <el-form-item
                            :error="floorPriceError(row)"
                            class="layer-floor-item"
                        >
                            <el-input-number
                                v-model="row.floorPrice"
                                :min="0.01"
                                :step="0.01"
                                :precision="2"
                                controls-position="right"
                                placeholder="底价"
                                :disabled="row.deleted || editorWriteLocked"
                                style="width: 120px"
                            />
                        </el-form-item>
                        <el-form-item
                            :error="layerNameError(row)"
                            class="layer-name-item"
                        >
                            <el-input
                                v-model="row.displayName"
                                :placeholder="rowMode(row) === 'new' ? '层名称' : '行名称'"
                                :disabled="row.deleted || rowMode(row) !== 'new' || editorWriteLocked"
                                style="width: 140px"
                            />
                        </el-form-item>
                        <el-input
                            :model-value="row.adUnitId || ''"
                            :placeholder="adUnitIdPlaceholder(row)"
                            disabled
                            style="width: 180px"
                        />
                        <el-popover
                            v-if="!row.deleted && rowNeedsExtraConfig(row)"
                            placement="left"
                            :width="340"
                            trigger="click"
                            @show="onRowExtraConfigShow(row)"
                        >
                            <template #reference>
                                <el-button link type="primary">额外配置</el-button>
                            </template>
                            <div class="row-extra-config" style="display:flex; flex-direction:column; gap:10px;">
                                <template v-if="rowSourceId(row) === INMOBI_SOURCE_ID">
                                    <div>
                                        <div class="label" style="margin-bottom:4px;">InMobi 账号</div>
                                        <el-select
                                            :model-value="row.thirdPartyAccountId"
                                            filterable
                                            placeholder="选择账号"
                                            style="width: 100%"
                                            :disabled="inmobiAccounts.length <= 1"
                                            @change="onRowInmobiAccountChange(row, $event)"
                                        >
                                            <el-option
                                                v-for="acc in inmobiAccounts"
                                                :key="acc.id"
                                                :label="acc.label"
                                                :value="acc.id"
                                            />
                                        </el-select>
                                    </div>
                                    <div>
                                        <div class="label" style="margin-bottom:4px;">InMobi App</div>
                                        <el-select
                                            v-model="row.partnerAppId"
                                            filterable
                                            clearable
                                            placeholder="选择 App"
                                            style="width: 100%"
                                            @change="onRowPartnerAppChange(row)"
                                        >
                                            <el-option
                                                v-for="app in inmobiApps"
                                                :key="app.id"
                                                :label="app.label"
                                                :value="app.id"
                                            />
                                        </el-select>
                                    </div>
                                </template>
                                <template v-else-if="rowSourceId(row) === PANGLE_SOURCE_ID">
                                    <div>
                                        <div class="label" style="margin-bottom:4px;">Pangle App</div>
                                        <el-select
                                            v-model="row.partnerAppId"
                                            filterable
                                            clearable
                                            placeholder="选择 App"
                                            style="width: 100%"
                                            @change="onRowPartnerAppChange(row)"
                                        >
                                            <el-option
                                                v-for="app in pangleApps"
                                                :key="app.id"
                                                :label="app.label"
                                                :value="app.id"
                                            />
                                        </el-select>
                                    </div>
                                </template>
                                <template v-else-if="rowSourceId(row) === VUNGLE_SOURCE_ID">
                                    <div>
                                        <div class="label" style="margin-bottom:4px;">Vungle App</div>
                                        <el-select
                                            v-model="row.partnerAppId"
                                            filterable
                                            clearable
                                            placeholder="选择 App"
                                            style="width: 100%"
                                            @change="onRowPartnerAppChange(row)"
                                        >
                                            <el-option
                                                v-for="app in vungleApps"
                                                :key="app.id"
                                                :label="app.label"
                                                :value="app.id"
                                            />
                                        </el-select>
                                    </div>
                                </template>
                                <div v-if="rowExtraConfigHint(row)" class="hint-line">{{ rowExtraConfigHint(row) }}</div>
                            </div>
                        </el-popover>
                        <el-button
                            v-if="row.deleted"
                            link
                            type="primary"
                            :disabled="editorWriteLocked"
                            @click="restoreRow(row)"
                        >撤销</el-button>
                        <el-button
                            v-else
                            type="danger"
                            link
                            :disabled="editorWriteLocked"
                            @click="markDeleted(row)"
                        >删除</el-button>
                    </div>

                    </template>
                </div>
            </div>

            <el-dialog
                v-model="importDialogOpen"
                title="导入其他中介组瀑布流"
                width="480px"
                append-to-body
            >
                <div class="hint-line" style="margin-bottom: 12px">
                    选择当前账号、当前应用下的其他中介组，将其瀑布流拷贝为新建层（全部新建，不绑定已有对方单元）。
                </div>
                <el-select
                    v-model="importSourceGroupId"
                    filterable
                    placeholder="选择中介组"
                    style="width: 100%"
                >
                    <el-option
                        v-for="g in importableGroups"
                        :key="g.mediationGroupId"
                        :label="g.displayName || g.mediationGroupId"
                        :value="g.mediationGroupId"
                    />
                </el-select>
                <template #footer>
                    <el-button @click="importDialogOpen = false">取消</el-button>
                    <el-button
                        type="primary"
                        :loading="importLoading"
                        :disabled="!importSourceGroupId"
                        @click="confirmImportWaterfall"
                    >导入</el-button>
                </template>
            </el-dialog>

            <el-dialog
                v-model="targetingEditorOpen"
                title="修改普通广告单元"
                width="520px"
                append-to-body
            >
                <div class="hint-line" style="margin-bottom: 12px">
                    绑定中介组 targeting 的普通广告单元。格式 X 决定行上 format 锁定。当前 X={{ formatX || '未确定' }}
                </div>
                <el-select
                    v-model="draftTargetingAdUnitIds"
                    multiple
                    filterable
                    collapse-tags
                    collapse-tags-tooltip
                    placeholder="选择普通广告单元"
                    style="width: 100%"
                    @change="onTargetingChange"
                >
                    <el-option
                        v-for="unit in standardAdUnits"
                        :key="unit.adUnitId"
                        :label="`${unit.displayName || unit.adUnitId} [${unit.adFormat}]`"
                        :value="unit.adUnitId"
                    />
                </el-select>
                <template #footer>
                    <el-button type="primary" @click="targetingEditorOpen = false">完成</el-button>
                </template>
            </el-dialog>

            <template #footer>
                <div class="editor-footer">
                    <el-button @click="editorOpen = false">取消</el-button>
                    <el-button
                        type="primary"
                        :loading="confirming"
                        :disabled="editorWriteLocked"
                        @click="openConfirm"
                    >确认变更</el-button>
                </div>
            </template>
        </el-dialog>

        <el-dialog
            v-model="abCreateOpen"
            title="创建 AB 测试"
            width="640px"
            append-to-body
            destroy-on-close
            :close-on-click-modal="!abCreateSubmitting"
            :close-on-press-escape="!abCreateSubmitting"
            :show-close="!abCreateSubmitting"
        >
            <div
                v-loading="abCreateSubmitting"
                :element-loading-text="abCreateLoadingText"
                class="ab-dialog-body"
            >
            <div class="hint-line" style="margin-bottom:12px">
                对照 A = 当前中介组瀑布流（只读）。下方只改实验组 B 的 MANUAL 底价；LIVE 等将原样提交。
            </div>
            <el-form label-width="110px" :disabled="abCreateSubmitting">
                <el-form-item label="实验名称" required>
                    <el-input v-model="abDisplayName" placeholder="实验显示名称" />
                </el-form-item>
                <el-form-item label="B 流量 %" required>
                    <el-input-number
                        v-model="abTrafficPct"
                        :min="1"
                        :max="99"
                        :step="1"
                        controls-position="right"
                    />
                </el-form-item>
            </el-form>
            <div class="section-label" style="margin:8px 0">实验组 B · MANUAL 底价</div>
            <div
                v-for="over in abFloorOverrides"
                :key="over.lineId"
                style="display:flex; gap:12px; align-items:center; margin-bottom:8px"
            >
                <span style="flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap">
                    {{ abUpstreamLineLabel(over.lineId) }}
                </span>
                <el-input-number
                    v-model="over.floorPrice"
                    :min="0.01"
                    :step="0.01"
                    :precision="2"
                    controls-position="right"
                    style="width: 140px"
                    :disabled="abCreateSubmitting"
                />
            </div>
            <div v-if="!abFloorOverrides.length" class="hint-line">当前组没有 MANUAL 底价层可改。</div>
            <div class="section-label" style="margin:12px 0 8px">将原样带入的 LIVE 等</div>
            <ul v-if="abLiveUpstreamLines.length" style="margin:0; padding-left:18px">
                <li v-for="line in abLiveUpstreamLines" :key="String(line.id || line.displayName)">
                    {{ line.displayName || line.display_name || line.id }}
                    ({{ String(line.cpmMode || line.cpm_mode || '').toUpperCase() }})
                </li>
            </ul>
            <div v-else class="hint-line">无 LIVE 行</div>
            <el-alert
                v-if="abCreateError"
                type="error"
                :title="abCreateError"
                show-icon
                :closable="false"
                style="margin-top:12px"
            />
            </div>
            <template #footer>
                <el-button :disabled="abCreateSubmitting" @click="abCreateOpen = false">取消</el-button>
                <el-button
                    type="primary"
                    :loading="abCreateSubmitting"
                    :disabled="abCreateSubmitting"
                    @click="submitCreateAbExperiment"
                >创建</el-button>
            </template>
        </el-dialog>

        <el-dialog
            v-model="abStopOpen"
            title="停止 AB 测试"
            width="480px"
            append-to-body
            destroy-on-close
            :close-on-click-modal="!abStopSubmitting"
            :close-on-press-escape="!abStopSubmitting"
            :show-close="!abStopSubmitting"
        >
            <div
                v-loading="abStopSubmitting"
                :element-loading-text="abStopLoadingText"
                class="ab-dialog-body"
            >
            <div class="hint-line" style="margin-bottom:12px">停止后需选择保留哪一侧配置。</div>
            <el-radio-group v-model="abVariantChoice" :disabled="abStopSubmitting">
                <el-radio label="VARIANT_CHOICE_A">保留对照 A（创建前原线路）</el-radio>
                <el-radio label="VARIANT_CHOICE_B">保留实验组 B</el-radio>
            </el-radio-group>
            <el-alert
                v-if="abVariantChoice === 'VARIANT_CHOICE_B'"
                type="warning"
                title="将用实验组配置覆盖线上（100% 流量），这是生产配置变更。"
                show-icon
                :closable="false"
                style="margin-top:12px"
            />
            <el-alert
                v-if="abStopError"
                type="error"
                :title="abStopError"
                show-icon
                :closable="false"
                style="margin-top:12px"
            />
            </div>
            <template #footer>
                <el-button :disabled="abStopSubmitting" @click="abStopOpen = false">取消</el-button>
                <el-button
                    type="danger"
                    :loading="abStopSubmitting"
                    :disabled="abStopSubmitting"
                    @click="submitStopAbExperiment"
                >确认停止</el-button>
            </template>
        </el-dialog>

        <el-dialog
            v-model="confirmOpen"
            title="确认变更"
            width="560px"
            destroy-on-close
            @closed="onConfirmClosed"
        >
            <div v-if="confirmPlan.warnings.length" class="confirm-summary">
                <el-alert
                    v-for="(w, i) in confirmPlan.warnings"
                    :key="'w'+i"
                    :title="w"
                    type="warning"
                    show-icon
                    :closable="false"
                    style="margin-bottom: 8px"
                />
            </div>

            <div v-if="!confirmPlan.steps.length" class="hint-line">没有可提交的变更。</div>
            <div
                v-if="!confirmPlan.steps.length && confirmPlan.missingApis.length"
                class="confirm-summary"
            >
                <el-alert
                    v-for="note in confirmPlan.missingApis"
                    :key="note.id"
                    :title="note.message"
                    type="warning"
                    show-icon
                    :closable="false"
                    style="margin-bottom: 8px"
                />
            </div>
            <template v-else>
                <div class="confirm-summary">
                    共
                    <b>{{ confirmPlan.steps.length }}</b>
                    步：先执行 dry-run，全部通过后再 Apply 写入 AdMob。
                </div>
                <ul class="confirm-list">
                    <li v-for="step in confirmPlan.steps" :key="step.id">
                        <span class="confirm-list-label">{{ step.label }}</span>
                        <el-tag
                            v-if="confirmResultsMap[step.id]"
                            size="small"
                            :type="confirmResultsMap[step.id].ok ? (step.dependsOn ? 'info' : 'success') : 'danger'"
                            style="margin-left: 8px"
                        >
                            {{
                                confirmResultsMap[step.id].ok
                                    ? (step.dependsOn
                                        ? (confirmResultsMap[step.id].message || 'Apply 时串联校验')
                                        : 'dry-run 通过')
                                    : '失败'
                            }}
                        </el-tag>
                        <el-tag
                            v-if="applyResultsMap[step.id]"
                            size="small"
                            :type="applyResultsMap[step.id].ok ? 'success' : 'danger'"
                            style="margin-left: 8px"
                        >
                            {{ applyResultsMap[step.id].ok ? '已 apply' : 'apply 失败' }}
                        </el-tag>
                        <el-collapse
                            v-if="confirmResultsMap[step.id] && !confirmResultsMap[step.id].ok"
                            class="confirm-error-collapse"
                        >
                            <el-collapse-item
                                :title="summarizeErrorMessage(confirmResultsMap[step.id].message || '失败', 120)"
                                :name="`dry-${step.id}`"
                            >
                                <pre class="confirm-error-body">{{ confirmResultsMap[step.id].message }}</pre>
                            </el-collapse-item>
                        </el-collapse>
                        <el-collapse
                            v-if="applyResultsMap[step.id] && !applyResultsMap[step.id].ok"
                            class="confirm-error-collapse"
                        >
                            <el-collapse-item
                                :title="summarizeErrorMessage(applyResultsMap[step.id].message || 'apply 失败', 120)"
                                :name="`apply-${step.id}`"
                            >
                                <pre class="confirm-error-body">{{ applyResultsMap[step.id].message }}</pre>
                            </el-collapse-item>
                        </el-collapse>
                    </li>
                </ul>
            </template>

            <el-collapse v-if="confirmPlan.missingApis.length || confirmPlan.steps.length" class="confirm-tech">
                <el-collapse-item title="说明与限制" name="notes">
                    <div
                        v-for="note in confirmPlan.missingApis"
                        :key="note.id"
                        class="hint-line"
                    >{{ note.message }}</div>
                </el-collapse-item>
            </el-collapse>

            <template #footer>
                <el-button @click="confirmOpen = false">关闭</el-button>
                <el-button
                    type="danger"
                    :loading="applying"
                    :disabled="!canApply"
                    @click="runApplies"
                >
                    Apply 到 AdMob
                </el-button>
                <el-button
                    type="primary"
                    :loading="confirming"
                    :disabled="!canRetryDryRun"
                    @click="runDryRuns"
                >
                    执行 dry-run
                </el-button>
            </template>
        </el-dialog>

        <el-dialog
            v-model="createOpen"
            title="新建中介组"
            width="520px"
            destroy-on-close
            @closed="resetCreateForm"
        >
            <el-form label-width="100px" v-loading="createLoading">
                <el-form-item label="账号" required>
                    <el-select
                        v-if="accounts.length > 1"
                        v-model="createAccountId"
                        filterable
                        placeholder="选择账号"
                        style="width: 100%"
                        :disabled="createLoading || createAppsLoading"
                        @change="onCreateAccountChange"
                    >
                        <el-option
                            v-for="acc in accounts"
                            :key="acc.id"
                            :label="acc.label"
                            :value="acc.id"
                        />
                    </el-select>
                    <span v-else-if="accounts.length === 1">{{ accounts[0].label }}</span>
                    <span v-else class="hint-line">暂无账号</span>
                </el-form-item>
                <el-form-item label="应用" required>
                    <el-select
                        v-model="createAppId"
                        filterable
                        clearable
                        placeholder="选择应用"
                        style="width: 100%"
                        :disabled="createLoading || createAppsLoading || !createAccountId"
                        :loading="createAppsLoading"
                        @change="onCreateAppChange"
                    >
                        <el-option
                            v-for="app in createApps"
                            :key="app.appId"
                            :label="app.label"
                            :value="app.appId"
                        />
                    </el-select>
                </el-form-item>
                <el-form-item label="名称" required :error="createGroupNameTaken ? '名称已存在' : ''">
                    <el-input
                        v-model="createForm.displayName"
                        maxlength="120"
                        show-word-limit
                        placeholder="中介组名称"
                    />
                </el-form-item>
                <el-form-item label="平台" required>
                    <span>{{ createPlatformLabel }}</span>
                </el-form-item>
                <el-form-item label="广告格式" required>
                    <el-select
                        v-model="createForm.format"
                        style="width: 100%"
                        :loading="createFormatsLoading"
                        :disabled="createFormatsLoading || !createFormatOptions.length"
                        @change="createForm.adUnitId = ''"
                    >
                        <el-option
                            v-for="opt in createFormatOptions"
                            :key="opt.value"
                            :label="opt.label"
                            :value="opt.value"
                        />
                    </el-select>
                    <div v-if="createFormatsError" class="hint-line" style="margin-top: 6px; color: var(--el-color-danger)">
                        {{ createFormatsError }}
                    </div>
                </el-form-item>
                <el-form-item label="普通广告单元" required>
                    <div style="display: flex; gap: 8px; width: 100%">
                        <el-select
                            v-model="createForm.adUnitId"
                            filterable
                            clearable
                            placeholder="选择普通广告单元"
                            style="flex: 1"
                        >
                            <el-option
                                v-for="u in createCandidateUnits"
                                :key="u.adUnitId"
                                :label="`${u.displayName || u.adUnitId} (${u.adUnitId})`"
                                :value="u.adUnitId"
                            />
                        </el-select>
                        <el-button
                            :disabled="!canOpenCreateUnit || createLoading"
                            @click="openCreateUnit"
                        >
                            创建
                        </el-button>
                    </div>
                    <div class="hint-line" style="margin-top: 6px">
                        创建时会附带一条 AdMob Network（LIVE）默认 line；瀑布流分层请创建后「编辑瀑布流」配置。
                    </div>
                </el-form-item>
                <el-form-item label="状态">
                    <el-select v-model="createForm.state" style="width: 100%">
                        <el-option label="启用" value="ENABLED" />
                        <el-option label="停用" value="DISABLED" />
                    </el-select>
                </el-form-item>
                <el-form-item v-if="createDryRunId" label="dry-run">
                    <el-tag type="success" size="small">已通过，可 Apply</el-tag>
                </el-form-item>
                <el-form-item v-if="createError" label="错误">
                    <div class="hint-line">{{ createError }}</div>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="createOpen = false">取消</el-button>
                <el-button
                    type="danger"
                    :loading="createApplying"
                    :disabled="!createDryRunId || !createPlanHash"
                    @click="applyCreateGroup"
                >
                    Apply 到 AdMob
                </el-button>
                <el-button
                    type="primary"
                    :loading="createDryRunning"
                    :disabled="!canSubmitCreate"
                    @click="dryRunCreateGroup"
                >
                    执行 dry-run
                </el-button>
            </template>
        </el-dialog>

        <el-dialog
            v-model="createUnitOpen"
            title="创建标准广告单元"
            width="480px"
            append-to-body
            destroy-on-close
            @closed="resetCreateUnitForm"
        >
            <el-form label-width="100px" v-loading="createUnitDryRunning || createUnitApplying">
                <el-form-item label="应用">
                    <span>{{ createAppId }}</span>
                </el-form-item>
                <el-form-item label="广告格式">
                    <span>{{ createForm.format }}</span>
                </el-form-item>
                <el-form-item label="名称" required :error="createUnitNameTaken ? '名称已存在' : ''">
                    <el-input
                        v-model="createUnitForm.displayName"
                        maxlength="80"
                        show-word-limit
                        placeholder="广告单元名称"
                    />
                </el-form-item>
                <template v-if="createForm.format === 'REWARDED'">
                    <el-form-item label="奖励数量" required>
                        <el-input v-model="createUnitForm.unitAmount" placeholder="例如 10" />
                    </el-form-item>
                    <el-form-item label="奖励类型" required>
                        <el-input v-model="createUnitForm.unitType" placeholder="例如 coin" />
                    </el-form-item>
                </template>
                <el-form-item v-if="createUnitDryRunId" label="dry-run">
                    <el-tag type="success" size="small">已通过，可 Apply</el-tag>
                </el-form-item>
                <el-form-item v-if="createUnitError" label="错误">
                    <div class="hint-line">{{ createUnitError }}</div>
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="createUnitOpen = false">取消</el-button>
                <el-button
                    type="danger"
                    :loading="createUnitApplying"
                    :disabled="!createUnitDryRunId || !createUnitPlanHash"
                    @click="applyCreateUnit"
                >
                    Apply 到 AdMob
                </el-button>
                <el-button
                    type="primary"
                    :loading="createUnitDryRunning"
                    :disabled="!canSubmitCreateUnit"
                    @click="dryRunCreateUnit"
                >
                    执行 dry-run
                </el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import {Search} from '@element-plus/icons-vue';
import {ElMessage, ElMessageBox} from 'element-plus';
import {showHttpError, summarizeErrorMessage} from '../libs/errorDisplay';
import {tableAreaHeightMixin} from '../libs/tableAreaHeight';
import {admobApply, admobDryRun, admobGet, admobReport} from '../admob/client';
import {
    buildConfirmPlan,
    buildWaterfallOptions,
    collectFormatMismatchNotes,
    cpmMicrosToDollars,
    discardUncommittedDraftForAdSource,
    dollarsToCpmMicros,
    displayNameExists,
    mediationGroupMatchesApp,
    duplicateDisplayNameKeys,
    duplicateFloorPriceKeys,
    fillBoundOpmcIdsByDisplayName,
    filterOpmcUnitsForRow,
    filterOrdinaryAdUnits,
    filterAndSortMixedRows,
    hasDuplicateDisplayNames,
    hasDuplicateFloorPrices,
    hasUncommittedDraftForAdSource,
    indexOpmcIdByMappingName,
    inferOpmcBindMode,
    linesToDraftRows,
    lookupGroupMetrics,
    newDraftRow,
    parseMediationReportByGroup,
    recentDateRange,
    resolveTargetingFormat,
    rowsFromImportedWaterfall,
    withBoundOpmcIds,
} from '../admob/mediationDraft';
import {
    normalizeExperimentState,
    isExperimentRunning,
    defaultExperimentDisplayName,
    cloneManualFloorOverrides,
    buildTreatmentMediationLines,
    validateCreateExperimentInput,
    buildCreateExperimentChange,
    buildStopExperimentChange,
    partitionDraftRowsForAbView,
} from '../admob/mediationAbExperiment.ts';
import {
    ADMOB_NETWORK_SOURCE_ID,
    collectFormatsFromAdapters,
    formatOptionLabel,
    pickDefaultFormat,
} from '../admob/adFormats';
import {resolveAppPlatform, platformDisplayLabel} from '../admob/appPlatform';
import {buildCreateAdUnitChange, pickAdUnitIdAfterCreate} from '../admob/createAdUnit';
import {
    firstArray,
    pickAdmobAccountId,
    resolveMediationGroupLines,
    resourceIdFromName,
} from '../admob/responseExtract';
import {
    pendingConfirmSteps,
    canApplyConfirmSteps,
    mergeConfirmResults,
} from '../admob/confirmSession';
import {
    ADMOB_NETWORK_WATERFALL_SOURCE_ID,
    adUnitResourceIdFromName,
    buildOpmcMappingChange,
    buildWaterfallLineChange,
    mappingChangeHasOpmcConfig,
    parseMappingNameFromApplyData,
    parseOpmcIdFromApplyData,
} from '../admob/opmcWaterfall';
import {
    APPLOVIN_SDK_KEY_STORAGE,
    APPLOVIN_SOURCE_ID,
    INMOBI_SOURCE_ID,
    MINTEGRAL_SOURCE_ID,
    PANGLE_SOURCE_ID,
    VUNGLE_SOURCE_ID,
    buildApplovinMappingChange,
    buildInmobiMappingChange,
    buildMintegralMappingChange,
    buildPangleMappingChange,
    buildVungleMappingChange,
    defaultSelectedPlatformIds,
    getWaterfallSource,
    isSupportedWaterfallSource,
    listSupportedSourceIds,
    unionSupportedPlatformOptions,
    mappingChangeHasConfigs,
    mintegralAdType,
    parseInmobiPlacementIdFromApplyData,
    parseMaxAdUnitIdFromApplyData,
    parseMintegralUnitIdFromApplyData,
    parsePanglePlacementIdFromApplyData,
    parseVunglePlacementIdFromApplyData,
    pickWaterfallIdAfterReload,
} from '../admob/waterfallSources';

const ACCOUNT_STORAGE_KEY = 'monetization.admob.accountId';
const APP_STORAGE_KEY = 'monetization.admob.appId';

export default {
    name: 'MediationGroups',
    mixins: [tableAreaHeightMixin],
    data() {
        return {
            Search,
            loading: false,
            accounts: [],
            accountId: localStorage.getItem(ACCOUNT_STORAGE_KEY) || '',
            apps: [],
            appId: localStorage.getItem(APP_STORAGE_KEY) || '',
            /** adUnitId set belonging to selected app */
            appAdUnitIds: new Set(),
            groups: [],
            statusFilter: 'ENABLED',
            nameQuery: '',
            listHint: '',
            metricsByGroup: {},

            editorOpen: false,
            editorLoading: false,
            editingGroup: null,
            originalRows: [],
            draftRows: [],
            upstreamMediationLines: [],
            mediationAbExperimentState: 'UNKNOWN',
            allAdUnits: [],
            allOpmcUnits: [],
            draftTargetingAdUnitIds: [],
            originalTargetingAdUnitIds: [],
            groupAdFormat: '',
            groupAppId: '',
            adSourceId: '',
            adSourceNameById: {},
            adSourceCatalog: [],
            selectedWaterfallId: '',
            selectedPlatformIds: [],
            targetingEditorOpen: false,
            importDialogOpen: false,
            importSourceGroupId: '',
            importLoading: false,

            abCreateOpen: false,
            abDisplayName: '',
            abTrafficPct: 50,
            abFloorOverrides: [],
            abCreateSubmitting: false,
            abCreateError: '',
            abCreateLoadingText: '正在创建…',
            abStopOpen: false,
            abVariantChoice: 'VARIANT_CHOICE_A',
            abStopSubmitting: false,
            abStopError: '',
            abStopLoadingText: '正在停止…',

            /** Partner editor state (InMobi / AppLovin / Mintegral / Pangle / Vungle) */
            APPLOVIN_SOURCE_ID,
            INMOBI_SOURCE_ID,
            MINTEGRAL_SOURCE_ID,
            PANGLE_SOURCE_ID,
            VUNGLE_SOURCE_ID,
            applovinSdkKey: localStorage.getItem(APPLOVIN_SDK_KEY_STORAGE) || '',
            inmobiAccounts: [],
            inmobiApps: [],
            pangleApps: [],
            vungleApps: [],
            remoteUnits: [],
            mintegralAppKey: '',
            mintegralAppId: '',
            mintegralPlacements: [],
            mintegralPlacementId: '',

            confirmOpen: false,
            confirming: false,
            applying: false,
            confirmPlan: {steps: [], missingApis: [], warnings: []},
            confirmResults: [],
            applyResults: [],
            applyArtifacts: {},
            confirmRan: false,
            applyRan: false,
            _skipNextConfirmClosedRefresh: false,

            createOpen: false,
            createLoading: false,
            createDryRunning: false,
            createApplying: false,
            createAdUnits: [],
            createAdapters: [],
            createFormatsError: '',
            createFormatsLoading: false,
            createForm: {
                displayName: '',
                platform: '',
                format: '',
                adUnitId: '',
                state: 'ENABLED',
            },
            createAccountId: '',
            createAppId: '',
            createApps: [],
            createAppAdUnitIds: new Set(),
            createGroupsForNames: [],
            createAppsLoading: false,
            createDryRunId: '',
            createPlanHash: '',
            createError: '',
            createUnitOpen: false,
            createUnitForm: {
                displayName: '',
                unitAmount: '10',
                unitType: 'coin',
            },
            createUnitDryRunId: '',
            createUnitPlanHash: '',
            createUnitError: '',
            createUnitDryRunning: false,
            createUnitApplying: false,
        };
    },
    computed: {
        createPlatformLabel() {
            return platformDisplayLabel(this.createForm.platform);
        },
        createFormatOptions() {
            return collectFormatsFromAdapters(this.createAdapters, this.createForm.platform).map(
                (value) => ({value, label: formatOptionLabel(value)}),
            );
        },
        createCandidateUnits() {
            return filterOrdinaryAdUnits(
                this.createAdUnits,
                this.createForm.format,
                this.createAppId || undefined,
            );
        },
        canSubmitCreate() {
            return Boolean(
                this.createAccountId &&
                    this.createAppId &&
                    this.createForm.displayName.trim() &&
                    this.createForm.platform &&
                    this.createForm.format &&
                    this.createForm.adUnitId &&
                    !this.createGroupNameTaken,
            );
        },
        canOpenCreateUnit() {
            return Boolean(
                this.createAccountId && this.createAppId && this.createForm.format,
            );
        },
        canSubmitCreateUnit() {
            if (!this.canOpenCreateUnit) return false;
            if (!String(this.createUnitForm.displayName || '').trim()) return false;
            if (this.createUnitNameTaken) return false;
            if (this.createForm.format === 'REWARDED') {
                return Boolean(
                    String(this.createUnitForm.unitAmount || '').trim() &&
                        String(this.createUnitForm.unitType || '').trim(),
                );
            }
            return true;
        },
        createGroupNameTaken() {
            const names = (this.createGroupsForNames || []).map((g) => g.displayName || '');
            return displayNameExists(this.createForm.displayName, names);
        },
        createUnitNameTaken() {
            const names = (this.createAdUnits || []).map((u) => u.displayName || '');
            return displayNameExists(this.createUnitForm.displayName, names);
        },
        visibleDraftRows() {
            const filtered = filterAndSortMixedRows(this.draftRows, this.selectedPlatformIds);
            // 新建已删的直接移出数组；已有行软删除后仍展示（置灰看原文）
            return filtered.filter((r) => {
                if (!r.deleted) {
                    return true;
                }
                return Boolean(r.isExisting || r.lineId);
            });
        },
        importableGroups() {
            const currentId = String(this.editingGroup?.mediationGroupId || '');
            return (this.groups || []).filter((g) => {
                if (!g?.mediationGroupId || String(g.mediationGroupId) === currentId) {
                    return false;
                }
                return this.groupMatchesApp(g);
            });
        },
        /** 当前瀑布流里仍可编辑的行（不含置灰） */
        activeDraftRows() {
            return this.visibleDraftRows.filter((r) => !r.deleted);
        },
        standardAdUnitLabel() {
            if (!this.draftTargetingAdUnitIds.length) {
                return '{未选择}';
            }
            const id = this.draftTargetingAdUnitIds[0];
            const unit = this.allAdUnits.find((u) => u.adUnitId === id);
            const name = unit?.displayName || id;
            const extra =
                this.draftTargetingAdUnitIds.length > 1
                    ? ` 等 ${this.draftTargetingAdUnitIds.length} 个`
                    : '';
            return `${name}${extra}`;
        },
        standardAdUnits() {
            return filterOrdinaryAdUnits(
                this.allAdUnits,
                this.formatX || this.groupAdFormat,
                this.groupAppId || undefined,
            );
        },
        formatX() {
            return resolveTargetingFormat(
                this.draftTargetingAdUnitIds,
                this.allAdUnits,
                this.groupAdFormat,
            );
        },
        confirmResultsMap() {
            const map = {};
            for (const item of this.confirmResults) {
                map[item.id] = item;
            }
            return map;
        },
        applyResultsMap() {
            const map = {};
            for (const item of this.applyResults) {
                map[item.id] = item;
            }
            return map;
        },
        canApply() {
            return canApplyConfirmSteps(
                this.confirmPlan.steps,
                this.confirmResults,
                this.applyResults,
            );
        },
        canRetryDryRun() {
            return pendingConfirmSteps(this.confirmPlan.steps, this.applyResults).length > 0;
        },
        experimentRunning() {
            return isExperimentRunning(this.mediationAbExperimentState);
        },
        editorWriteLocked() {
            return this.experimentRunning;
        },
        abLiveUpstreamLines() {
            return (this.upstreamMediationLines || []).filter((line) => {
                const mode = String(line?.cpmMode || line?.cpm_mode || '').toUpperCase();
                return mode === 'LIVE';
            });
        },
        abVariantARows() {
            const {variantA} = partitionDraftRowsForAbView(this.draftRows || []);
            return filterAndSortMixedRows(variantA, this.selectedPlatformIds).filter(
                (r) => !r.deleted || Boolean(r.isExisting || r.lineId),
            );
        },
        abVariantBRows() {
            const {variantB} = partitionDraftRowsForAbView(this.draftRows || []);
            return filterAndSortMixedRows(variantB, this.selectedPlatformIds).filter(
                (r) => !r.deleted || Boolean(r.isExisting || r.lineId),
            );
        },
        filteredGroups() {
            const q = (this.nameQuery || '').trim().toLowerCase();
            return this.groups.filter((g) => {
                if (this.statusFilter !== 'ALL' && g.state !== this.statusFilter) {
                    return false;
                }
                if (q) {
                    const name = String(g.displayName || g.mediationGroupId || '').toLowerCase();
                    if (!name.includes(q)) {
                        return false;
                    }
                }
                if (this.appId) {
                    if (!this.groupMatchesApp(g)) {
                        return false;
                    }
                }
                return true;
            });
        },
        adSourceOptions() {
            return buildWaterfallOptions(
                this.draftRows,
                this.adSourceNameById,
                this.adSourceCatalog,
            );
        },
        waterfallPanelOptions() {
            const supported = listSupportedSourceIds().map((id) => ({
                id,
                name: getWaterfallSource(id)?.displayNameHint || id,
            }));
            return unionSupportedPlatformOptions(this.adSourceOptions || [], supported);
        },
        selectedWaterfallAdapter() {
            return getWaterfallSource(this.selectedWaterfallId) || null;
        },
        selectedSourceLabel() {
            const adapter = this.selectedWaterfallAdapter;
            if (adapter?.displayNameHint) {
                return adapter.displayNameHint;
            }
            const opt = (this.waterfallPanelOptions || []).find(
                (o) => o.id === this.selectedWaterfallId,
            );
            return opt?.name || '瀑布流';
        },
        selectedSourceTag() {
            const hint = this.selectedWaterfallAdapter?.displayNameHint;
            if (this.selectedWaterfallId === ADMOB_NETWORK_WATERFALL_SOURCE_ID) {
                return 'OPMC · MANUAL';
            }
            return hint ? `${hint} · MANUAL` : 'MANUAL';
        },
        selectedSourceHint() {
            return '新建层只能填写平台、层名称与底价并创建对方单元；已有行可改底价。确认 Apply 后按行所属平台串联创建。';
        },
        remotePickPlaceholder() {
            const id = this.selectedWaterfallId;
            if (id === ADMOB_NETWORK_WATERFALL_SOURCE_ID) return '选择已有 OPMC';
            if (id === INMOBI_SOURCE_ID) return '选择已有 Placement';
            if (id === APPLOVIN_SOURCE_ID) return '选择已有 MAX Ad Unit';
            if (id === MINTEGRAL_SOURCE_ID) return '选择已有 Unit';
            return '选择已有对方单元';
        },
        opmcCandidateUnits() {
            const appId = String(this.groupAppId || '').trim();
            const format = String(this.formatX || this.groupAdFormat || '').toUpperCase();
            return (this.allOpmcUnits || []).filter((unit) => {
                if (appId && String(unit.appId || '').trim() !== appId) {
                    return false;
                }
                const unitFormat = String(unit.format || unit.adFormat || '').toUpperCase();
                if (format && unitFormat && unitFormat !== format) {
                    return false;
                }
                return Boolean(this.opmcUnitId(unit));
            });
        },
        appOpmcDisplayNames() {
            const appId = String(this.groupAppId || '').trim();
            return (this.allOpmcUnits || [])
                .filter((u) => !appId || String(u.appId || '').trim() === appId)
                .map((u) => u.displayName || u.name || '');
        },
        /** Force el-select to refresh options when other rows occupy OPMC ids/names. */
        opmcPickFingerprint() {
            const parts = [];
            for (const row of this.draftRows || []) {
                if (row.deleted) {
                    continue;
                }
                const id =
                    String(row.selectedOpmcId || '').trim() ||
                    String(row.boundOpmcId || '').trim();
                const name = String(row.displayName || '').trim();
                if (id || name) {
                    parts.push(`${row.key}:${id}:${name}`);
                }
            }
            return parts.sort().join('|');
        },
    },
    mounted() {
        this.loadAccounts();
    },
    methods: {
        summarizeErrorMessage,
        formatMoney(v) {
            if (v == null || Number.isNaN(Number(v))) {
                return '-';
            }
            return `$${Number(v).toFixed(2)}`;
        },
        formatCount(v) {
            if (v == null || Number.isNaN(Number(v))) {
                return '-';
            }
            return String(v);
        },
        opmcUnitId(unit) {
            return (
                String(unit?.admobNetworkWaterfallAdUnitId || '').trim() ||
                resourceIdFromName(unit?.name)
            );
        },
        opmcUnitLabel(unit) {
            const id = this.opmcUnitId(unit);
            const name = String(unit?.displayName || unit?.name || id || '').trim();
            return name && name !== id ? `${name} (${id})` : id;
        },
        resolveAppPackageName() {
            const appId = String(this.groupAppId || this.appId || '').trim();
            const row = (this.apps || []).find((a) => String(a.appId) === appId);
            const raw = row?.raw || {};
            return String(
                raw.linkedAppInfo?.appStoreId ||
                    raw.manualAppInfo?.appStoreId ||
                    raw.appStoreId ||
                    raw.packageName ||
                    raw.package_name ||
                    '',
            ).trim();
        },
        rowSourceTag(row) {
            const id = String(row?.adSourceId || row?.waterfallId || this.selectedWaterfallId || '').trim();
            if (id === ADMOB_NETWORK_WATERFALL_SOURCE_ID) return 'OPMC · MANUAL';
            const adapter = getWaterfallSource(id);
            const hint = adapter?.displayNameHint;
            return hint ? `${hint} · MANUAL` : 'MANUAL';
        },
        remoteUnitId(unit) {
            const id = this.selectedWaterfallId;
            if (id === ADMOB_NETWORK_WATERFALL_SOURCE_ID) {
                return this.opmcUnitId(unit);
            }
            if (id === INMOBI_SOURCE_ID) {
                return String(unit?.placementId || unit?.placement_id || unit?.id || '').trim();
            }
            if (id === APPLOVIN_SOURCE_ID) {
                return String(unit?.id || unit?.ad_unit_id || unit?.adUnitId || '').trim();
            }
            if (id === MINTEGRAL_SOURCE_ID) {
                return String(unit?.unit_id || unit?.unitId || unit?.id || '').trim();
            }
            if (id === PANGLE_SOURCE_ID) {
                return String(
                    unit?.placement_id ||
                        unit?.placementId ||
                        unit?.ad_slot_id ||
                        unit?.adSlotId ||
                        unit?.id ||
                        '',
                ).trim();
            }
            if (id === VUNGLE_SOURCE_ID) {
                return String(
                    unit?.referenceId ||
                        unit?.reference_id ||
                        unit?.id ||
                        unit?.placement_id ||
                        unit?.placementId ||
                        '',
                ).trim();
            }
            return String(unit?.id || '').trim();
        },
        remoteUnitLabel(unit) {
            const id = this.remoteUnitId(unit);
            const name = String(
                unit?.displayName ||
                    unit?.placementName ||
                    unit?.placement_name ||
                    unit?.ad_slot_name ||
                    unit?.adSlotName ||
                    unit?.name ||
                    unit?.unit_name ||
                    unit?.unitName ||
                    id ||
                    '',
            ).trim();
            return name && name !== id ? `${name} (${id})` : id;
        },
        remoteOptionsForRow(row) {
            if (this.selectedWaterfallId === ADMOB_NETWORK_WATERFALL_SOURCE_ID) {
                return this.opmcOptionsForRow(row);
            }
            return filterOpmcUnitsForRow(
                this.remoteUnits,
                this.draftRows,
                row,
                (unit) => this.remoteUnitId(unit),
                (unit) => this.remoteUnitLabel(unit),
            );
        },
        opmcOptionsForRow(row) {
            return filterOpmcUnitsForRow(
                this.opmcCandidateUnits,
                this.draftRows,
                row,
                (unit) => this.opmcUnitId(unit),
                (unit) => String(unit?.displayName || '').trim(),
            );
        },
        isDuplicateLayerName(row) {
            if (!row || row.deleted || this.editorWriteLocked) {
                return false;
            }
            return duplicateDisplayNameKeys(this.draftRows).has(row.key);
        },
        isDuplicateFloorPrice(row) {
            if (!row || row.deleted || this.editorWriteLocked) {
                return false;
            }
            return duplicateFloorPriceKeys(this.draftRows).has(row.key);
        },
        floorPriceError(row) {
            if (this.isDuplicateFloorPrice(row)) {
                return '底价不能重复';
            }
            return '';
        },
        isExistingOpmcLayerName(row) {
            if (!row || row.deleted) {
                return false;
            }
            if (this.rowMode(row) !== 'new') {
                return false;
            }
            if (row.opmcBindMode === 'existing') {
                return false;
            }
            const sourceId = String(row.adSourceId || row.waterfallId || this.selectedWaterfallId || '');
            if (sourceId !== ADMOB_NETWORK_WATERFALL_SOURCE_ID) {
                return false;
            }
            return displayNameExists(row.displayName, this.appOpmcDisplayNames);
        },
        layerNameError(row) {
            if (this.isExistingOpmcLayerName(row)) {
                return '名称已存在';
            }
            if (this.isDuplicateLayerName(row)) {
                return '层名称不能重复';
            }
            return '';
        },
        hasExistingOpmcLayerName() {
            return this.draftRows.some((row) => this.isExistingOpmcLayerName(row));
        },
        async onPickWaterfallSource(id) {
            const next = String(id || '').trim();
            if (!next) {
                if (this.selectedWaterfallId) {
                    await this.selectAdSource('');
                }
                return;
            }
            if (!isSupportedWaterfallSource(next)) {
                return;
            }
            await this.selectAdSource(next);
        },
        async onPlatformFilterChange(ids) {
            const next = (ids || []).map(String).filter(Boolean);
            this.selectedPlatformIds = next;
            if (next.length === 1) {
                this.selectedWaterfallId = next[0];
                await this.loadPartnerRemoteOptions();
            } else {
                this.selectedWaterfallId = '';
            }
        },
        onRowPlatformChange(row, platformId) {
            let id = String(platformId || '').trim();
            if (
                id === ADMOB_NETWORK_SOURCE_ID ||
                id === ADMOB_NETWORK_WATERFALL_SOURCE_ID
            ) {
                id = ADMOB_NETWORK_WATERFALL_SOURCE_ID;
            }
            row.waterfallId = id;
            row.adSourceId = id;
            row.opmcBindMode = 'create';
            row.selectedOpmcId = '';
            row.boundOpmcId = '';
            row.thirdPartyAccountId = '';
            row.partnerAppId = '';
            if (id && !this.selectedPlatformIds.includes(id)) {
                this.selectedPlatformIds = [...this.selectedPlatformIds, id];
            }
        },
        openImportDialog() {
            if (this.editorWriteLocked) {
                return;
            }
            this.importSourceGroupId = '';
            this.importDialogOpen = true;
        },
        abUpstreamLineLabel(lineId) {
            const id = String(lineId || '');
            const line = (this.upstreamMediationLines || []).find(
                (l) => String(l.id || l.lineId || '') === id,
            );
            if (!line) {
                return id || '未知行';
            }
            return String(line.displayName || line.display_name || id);
        },
        editorHasUnsavedChanges() {
            if (!this.editingGroup) {
                return false;
            }
            const plan = buildConfirmPlan({
                accountId: this.accountId,
                mediationGroupId: this.editingGroup.mediationGroupId,
                appId: this.groupAppId,
                adFormat: this.formatX || this.groupAdFormat || 'BANNER',
                adTypes: (this.formatX || this.groupAdFormat) === 'REWARDED' ? ['VIDEO'] : ['RICH_MEDIA'],
                adSourceId: this.adSourceId,
                originalRows: this.originalRows,
                draftRows: this.draftRows,
                originalTargetingAdUnitIds: this.originalTargetingAdUnitIds,
                draftTargetingAdUnitIds: this.draftTargetingAdUnitIds,
                standardAdUnitResourceId: this.resolveStandardAdUnitResourceId() || undefined,
                platform: resolveAppPlatform(this.apps, this.groupAppId || this.appId),
                packageName: this.resolveAppPackageName() || undefined,
                applovinSdkKey: this.applovinSdkKey || undefined,
                mintegralAppKey: this.mintegralAppKey || undefined,
                mintegralAppId: this.mintegralAppId || undefined,
                mintegralPlacementId: this.mintegralPlacementId || undefined,
            });
            return (plan.steps || []).length > 0;
        },
        openCreateAbDialog() {
            if (this.editorWriteLocked) {
                return;
            }
            if (this.editorHasUnsavedChanges()) {
                ElMessage.warning('请先 Apply 或丢弃当前瀑布流草稿，再创建 AB 测试');
                return;
            }
            if (normalizeExperimentState(this.mediationAbExperimentState) !== 'NOT_RUNNING') {
                ElMessage.warning('仅在无运行中实验时可创建');
                return;
            }
            this.abFloorOverrides = cloneManualFloorOverrides(this.upstreamMediationLines);
            this.abDisplayName = defaultExperimentDisplayName(
                this.editingGroup?.displayName || this.editingGroup?.mediationGroupId || '',
            );
            this.abTrafficPct = 50;
            this.abCreateError = '';
            this.abCreateOpen = true;
        },
        openStopAbDialog() {
            this.abVariantChoice = 'VARIANT_CHOICE_A';
            this.abStopError = '';
            this.abStopOpen = true;
        },
        async submitCreateAbExperiment() {
            if (this.abCreateSubmitting) {
                return;
            }
            const treatmentMediationLines = buildTreatmentMediationLines(
                this.upstreamMediationLines,
                this.abFloorOverrides,
            );
            const errors = validateCreateExperimentInput({
                displayName: this.abDisplayName,
                treatmentTrafficPercentage: this.abTrafficPct,
                treatmentMediationLines,
                experimentState: this.mediationAbExperimentState,
            });
            if (errors.length) {
                this.abCreateError = errors.join('；');
                return;
            }
            const change = buildCreateExperimentChange({
                displayName: this.abDisplayName,
                treatmentTrafficPercentage: this.abTrafficPct,
                treatmentMediationLines,
            });
            const path = `/admob/accounts/${this.accountId}/mediation-groups/${this.editingGroup.mediationGroupId}/experiment`;
            this.abCreateSubmitting = true;
            this.abCreateError = '';
            this.abCreateLoadingText = '正在校验（dry-run）…';
            try {
                const dry = await admobDryRun('POST', path, change);
                const data = dry.upstream?.data || {};
                const dryRunId = String(data.dry_run_id || '');
                const planHash = String(data.plan_hash || '');
                if (!dryRunId || !planHash) {
                    throw new Error('dry-run 未返回 dry_run_id / plan_hash');
                }
                this.abCreateLoadingText = '正在创建实验…';
                const applied = await admobApply('POST', path, dryRunId, planHash);
                const warn =
                    applied.upstream?.meta?.warnings ||
                    applied.upstream?.data?.warnings ||
                    [];
                if (Array.isArray(warn) && warn.length) {
                    ElMessage.warning(String(warn[0]));
                } else {
                    ElMessage.success('AB 测试已创建');
                }
                this.abCreateLoadingText = '正在刷新瀑布流…';
                await this.reloadEditor({refreshCache: true});
                this.abCreateOpen = false;
            } catch (err) {
                this.abCreateError = err?.message || String(err);
                showHttpError(err, '创建 AB 测试失败');
            } finally {
                this.abCreateSubmitting = false;
                this.abCreateLoadingText = '正在创建…';
            }
        },
        async submitStopAbExperiment() {
            if (this.abStopSubmitting) {
                return;
            }
            const change = buildStopExperimentChange(this.abVariantChoice);
            const path = `/admob/accounts/${this.accountId}/mediation-groups/${this.editingGroup.mediationGroupId}/experiment`;
            this.abStopSubmitting = true;
            this.abStopError = '';
            this.abStopLoadingText = '正在校验（dry-run）…';
            try {
                const dry = await admobDryRun('POST', path, change);
                const data = dry.upstream?.data || {};
                const dryRunId = String(data.dry_run_id || '');
                const planHash = String(data.plan_hash || '');
                if (!dryRunId || !planHash) {
                    throw new Error('dry-run 未返回 dry_run_id / plan_hash');
                }
                this.abStopLoadingText = '正在停止实验…';
                await admobApply('POST', path, dryRunId, planHash);
                ElMessage.success(
                    this.abVariantChoice === 'VARIANT_CHOICE_B'
                        ? '已停止实验并保留实验组 B'
                        : '已停止实验并保留对照 A',
                );
                this.abStopLoadingText = '正在刷新瀑布流…';
                await this.reloadEditor({refreshCache: true});
                this.abStopOpen = false;
            } catch (err) {
                this.abStopError = err?.message || String(err);
                showHttpError(err, '停止 AB 测试失败');
            } finally {
                this.abStopSubmitting = false;
                this.abStopLoadingText = '正在停止…';
            }
        },
        async confirmImportWaterfall() {
            const gid = String(this.importSourceGroupId || '').trim();
            if (!gid || !this.accountId) {
                return;
            }
            this.importLoading = true;
            try {
                const waterfallRes = await admobGet(
                    `/admob/accounts/${this.accountId}/mediation-groups/${gid}/waterfall`,
                );
                const lines = resolveMediationGroupLines(waterfallRes.upstream?.data);

                // Drop local new drafts; soft-delete existing lines (gray, sink to bottom)
                const keptExisting = (this.draftRows || [])
                    .filter((r) => r.isExisting || r.lineId)
                    .map((r) => ({...r, deleted: true}));

                const usedNamesBySource = new Map();
                for (const name of this.appOpmcDisplayNames || []) {
                    const n = String(name || '').trim();
                    if (!n) {
                        continue;
                    }
                    if (!usedNamesBySource.has(ADMOB_NETWORK_WATERFALL_SOURCE_ID)) {
                        usedNamesBySource.set(ADMOB_NETWORK_WATERFALL_SOURCE_ID, new Set());
                    }
                    usedNamesBySource.get(ADMOB_NETWORK_WATERFALL_SOURCE_ID).add(n);
                }
                const {rows, skipped} = rowsFromImportedWaterfall({
                    lines,
                    startIndex: keptExisting.length,
                    usedNamesBySource,
                    isSupportedSource: (id) => isSupportedWaterfallSource(id),
                });
                this.draftRows = [...rows, ...keptExisting];
                this.importDialogOpen = false;
                if (rows.length) {
                    ElMessage.success(
                        `已导入 ${rows.length} 层；原有层已标记删除（草稿新建已清除）`,
                    );
                } else {
                    ElMessage.warning('没有可导入的分层');
                }
                if (skipped > 0) {
                    ElMessage.warning(`已跳过 ${skipped} 层（不支持的广告源）`);
                }
            } catch (err) {
                showHttpError(err, '导入瀑布流失败');
            } finally {
                this.importLoading = false;
            }
        },
        onSelectExistingOpmc(row, opmcId) {
            this.onSelectExistingRemote(row, opmcId);
        },
        onSelectExistingRemote(row, remoteId) {
            const id = String(remoteId || '').trim();
            row.selectedOpmcId = id;
            row.opmcBindMode = inferOpmcBindMode(id);
            if (!id) {
                return;
            }
            const unit = (this.remoteOptionsForRow(row) || []).find(
                (u) => this.remoteUnitId(u) === id,
            ) || (this.allOpmcUnits || []).find((u) => this.opmcUnitId(u) === id);
            if (!unit) {
                return;
            }
            const displayName = String(
                unit.displayName ||
                    unit.placementName ||
                    unit.placement_name ||
                    unit.name ||
                    unit.unit_name ||
                    unit.unitName ||
                    '',
            ).trim();
            if (displayName) {
                row.displayName = displayName;
            }
            const micros =
                unit.cpmFloorSettings?.globalFloorMicros ??
                unit.cpm_floor_settings?.global_floor_micros;
            const dollars =
                micros != null
                    ? cpmMicrosToDollars(micros)
                    : unit.cpmFloor != null
                      ? Number(unit.cpmFloor)
                      : null;
            if (dollars != null && !Number.isNaN(dollars)) {
                row.floorPrice = dollars;
            }
        },
        persistApplovinSdkKey() {
            const key = String(this.applovinSdkKey || '').trim();
            this.applovinSdkKey = key;
            if (key) {
                localStorage.setItem(APPLOVIN_SDK_KEY_STORAGE, key);
            } else {
                localStorage.removeItem(APPLOVIN_SDK_KEY_STORAGE);
            }
        },
        rowSourceId(row) {
            return String(row?.adSourceId || row?.waterfallId || '').trim();
        },
        rowNeedsExtraConfig(row) {
            const id = this.rowSourceId(row);
            return (
                id === INMOBI_SOURCE_ID ||
                id === PANGLE_SOURCE_ID ||
                id === VUNGLE_SOURCE_ID
            );
        },
        rowExtraConfigHint(row) {
            const id = this.rowSourceId(row);
            if (id === INMOBI_SOURCE_ID) {
                return '账号与 App 必选；Placement 仍在行上「选用已有」中选择。';
            }
            if (id === PANGLE_SOURCE_ID || id === VUNGLE_SOURCE_ID) {
                return 'App 必选；Placement 仍在行上「选用已有」中选择。';
            }
            return '';
        },
        async onRowExtraConfigShow(row) {
            const id = this.rowSourceId(row);
            if (id === INMOBI_SOURCE_ID) {
                await this.ensureInmobiAccounts();
                if (!row.thirdPartyAccountId && this.inmobiAccounts.length === 1) {
                    row.thirdPartyAccountId = this.inmobiAccounts[0].id;
                }
                if (row.thirdPartyAccountId) {
                    await this.loadInmobiAppsForAccount(row.thirdPartyAccountId, row);
                }
                return;
            }
            if (id === PANGLE_SOURCE_ID) {
                await this.loadPangleApps(row);
                return;
            }
            if (id === VUNGLE_SOURCE_ID) {
                await this.loadVungleApps(row);
            }
        },
        async onRowInmobiAccountChange(row, accountId) {
            row.thirdPartyAccountId = String(accountId || '').trim();
            row.partnerAppId = '';
            this.remoteUnits = [];
            if (row.thirdPartyAccountId) {
                await this.loadInmobiAppsForAccount(row.thirdPartyAccountId, row);
            } else {
                this.inmobiApps = [];
            }
        },
        async onRowPartnerAppChange(row) {
            this.remoteUnits = [];
            const id = this.rowSourceId(row);
            if (id === INMOBI_SOURCE_ID && row.thirdPartyAccountId && row.partnerAppId) {
                await this.loadInmobiPlacementsFor(row.thirdPartyAccountId, row.partnerAppId);
            } else if (id === PANGLE_SOURCE_ID && row.partnerAppId) {
                await this.loadPanglePlacements(row.partnerAppId);
            } else if (id === VUNGLE_SOURCE_ID && row.partnerAppId) {
                await this.loadVunglePlacements(row.partnerAppId);
            }
        },
        async onMintegralPlacementChange() {
            this.remoteUnits = [];
            await this.loadMintegralUnits();
        },
        async loadPartnerRemoteOptions() {
            const id = this.selectedWaterfallId;
            this.remoteUnits = [];
            if (id === INMOBI_SOURCE_ID) {
                await this.ensureInmobiAccounts();
                return;
            }
            if (id === APPLOVIN_SOURCE_ID) {
                await this.loadMaxAdUnits();
                return;
            }
            if (id === MINTEGRAL_SOURCE_ID) {
                await this.loadMintegralCascade();
                return;
            }
            if (id === PANGLE_SOURCE_ID) {
                await this.loadPangleApps();
                return;
            }
            if (id === VUNGLE_SOURCE_ID) {
                await this.loadVungleApps();
            }
        },
        async ensureInmobiAccounts() {
            try {
                const result = await admobGet('/inmobi/accounts');
                const list = firstArray(result.upstream?.data);
                this.inmobiAccounts = list
                    .map((item) => {
                        const id = String(
                            item.accountId || item.account_id || item.id || '',
                        ).trim();
                        const label = String(item.name || item.email || id).trim();
                        return {id, label: label && label !== id ? `${label} (${id})` : id};
                    })
                    .filter((a) => a.id);
            } catch (err) {
                showHttpError(err, '加载 InMobi 账号失败');
                this.inmobiAccounts = [];
            }
        },
        async loadInmobiAppsForAccount(accountId, row) {
            const acc = String(accountId || '').trim();
            if (!acc) {
                this.inmobiApps = [];
                return;
            }
            try {
                const result = await admobGet('/inmobi/apps', {account_id: acc});
                const list = firstArray(result.upstream?.data);
                this.inmobiApps = list
                    .map((item) => {
                        const id = String(item.appId || item.app_id || item.id || '').trim();
                        const name = String(item.appName || item.app_name || item.name || id).trim();
                        return {id, label: name && name !== id ? `${name} (${id})` : id, raw: item};
                    })
                    .filter((a) => a.id);
                if (row) {
                    if (row.partnerAppId && !this.inmobiApps.some((a) => a.id === row.partnerAppId)) {
                        row.partnerAppId = '';
                    }
                    if (!row.partnerAppId && this.inmobiApps.length === 1) {
                        row.partnerAppId = this.inmobiApps[0].id;
                    }
                    if (row.partnerAppId) {
                        await this.loadInmobiPlacementsFor(acc, row.partnerAppId);
                    }
                }
            } catch (err) {
                showHttpError(err, '加载 InMobi Apps 失败');
                this.inmobiApps = [];
            }
        },
        async loadInmobiPlacementsFor(accountId, appId) {
            if (!accountId || !appId) {
                this.remoteUnits = [];
                return;
            }
            try {
                const placementType = (() => {
                    const f = String(this.formatX || this.groupAdFormat || '').toUpperCase();
                    if (f === 'REWARDED') return 'REWARDED_VIDEO';
                    if (f === 'NATIVE') return 'NATIVE';
                    if (f === 'INTERSTITIAL') return 'INTERSTITIAL';
                    return 'BANNER';
                })();
                const result = await admobGet('/inmobi/placements', {
                    account_id: accountId,
                    appId,
                    placementType,
                    pageNum: 1,
                    pageLength: 100,
                });
                this.remoteUnits = firstArray(result.upstream?.data);
            } catch (err) {
                showHttpError(err, '加载 InMobi Placements 失败');
                this.remoteUnits = [];
            }
        },
        normalizePartnerApps(payload) {
            const list = firstArray(payload?.items || payload);
            return list
                .map((item) => {
                    const id = String(
                        item.app_id || item.appId || item.id || item.applicationId || '',
                    ).trim();
                    const name = String(
                        item.app_name || item.appName || item.name || id,
                    ).trim();
                    return {id, label: name && name !== id ? `${name} (${id})` : id, raw: item};
                })
                .filter((a) => a.id);
        },
        async loadPangleApps(row) {
            try {
                const result = await admobGet('/pangle/apps', {page: 1, page_size: 100});
                this.pangleApps = this.normalizePartnerApps(result.upstream?.data);
                if (row) {
                    if (row.partnerAppId && !this.pangleApps.some((a) => a.id === row.partnerAppId)) {
                        row.partnerAppId = '';
                    }
                    if (!row.partnerAppId && this.pangleApps.length === 1) {
                        row.partnerAppId = this.pangleApps[0].id;
                    }
                    if (row.partnerAppId) {
                        await this.loadPanglePlacements(row.partnerAppId);
                    }
                }
            } catch (err) {
                showHttpError(err, '加载 Pangle Apps 失败');
                this.pangleApps = [];
            }
        },
        async loadVungleApps(row) {
            try {
                const result = await admobGet('/vungle/apps', {page: 1, page_size: 100});
                this.vungleApps = this.normalizePartnerApps(result.upstream?.data);
                if (row) {
                    if (row.partnerAppId && !this.vungleApps.some((a) => a.id === row.partnerAppId)) {
                        row.partnerAppId = '';
                    }
                    if (!row.partnerAppId && this.vungleApps.length === 1) {
                        row.partnerAppId = this.vungleApps[0].id;
                    }
                    if (row.partnerAppId) {
                        await this.loadVunglePlacements(row.partnerAppId);
                    }
                }
            } catch (err) {
                showHttpError(err, '加载 Vungle Apps 失败');
                this.vungleApps = [];
            }
        },
        async loadPanglePlacements(appId) {
            if (!appId) {
                this.remoteUnits = [];
                return;
            }
            try {
                const result = await admobGet(`/pangle/apps/${appId}/placements`, {
                    page: 1,
                    page_size: 100,
                });
                this.remoteUnits = firstArray(result.upstream?.data?.items || result.upstream?.data);
            } catch (err) {
                showHttpError(err, '加载 Pangle Placements 失败');
                this.remoteUnits = [];
            }
        },
        async loadVunglePlacements(appId) {
            if (!appId) {
                this.remoteUnits = [];
                return;
            }
            try {
                const result = await admobGet(`/vungle/apps/${appId}/placements`, {
                    page: 1,
                    page_size: 100,
                });
                this.remoteUnits = firstArray(result.upstream?.data?.items || result.upstream?.data);
            } catch (err) {
                showHttpError(err, '加载 Vungle Placements 失败');
                this.remoteUnits = [];
            }
        },
        async loadMaxAdUnits() {
            try {
                const result = await admobGet('/max/ad-units', {limit: 100, offset: 0});
                const list = firstArray(result.upstream?.data);
                const pkg = this.resolveAppPackageName().toLowerCase();
                const platform = resolveAppPlatform(this.apps, this.groupAppId || this.appId).toLowerCase();
                const format = String(this.formatX || this.groupAdFormat || '').toUpperCase();
                const maxFmt =
                    format === 'INTERSTITIAL'
                        ? 'INTER'
                        : format === 'REWARDED'
                          ? 'REWARDED'
                          : format || '';
                this.remoteUnits = list.filter((unit) => {
                    if (pkg) {
                        const unitPkg = String(unit.package_name || unit.packageName || '').toLowerCase();
                        if (unitPkg && unitPkg !== pkg) return false;
                    }
                    if (platform) {
                        const unitPlat = String(unit.platform || '').toLowerCase();
                        if (unitPlat && unitPlat !== platform) return false;
                    }
                    if (maxFmt) {
                        const unitFmt = String(unit.ad_format || unit.adFormat || '').toUpperCase();
                        if (unitFmt && unitFmt !== maxFmt) return false;
                    }
                    return true;
                });
            } catch (err) {
                showHttpError(err, '加载 MAX Ad Units 失败');
                this.remoteUnits = [];
            }
        },
        async loadMintegralCascade() {
            this.mintegralAppKey = '';
            this.mintegralAppId = '';
            this.mintegralPlacements = [];
            this.mintegralPlacementId = '';
            this.remoteUnits = [];
            const pkg = this.resolveAppPackageName();
            const os = resolveAppPlatform(this.apps, this.groupAppId || this.appId) || 'ANDROID';
            if (!pkg) {
                ElMessage.warning('当前 AdMob App 无 package/bundle，无法匹配 Mintegral App');
                return;
            }
            try {
                const appsRes = await admobGet('/mintegral/apps', {package: pkg, os});
                const apps = firstArray(appsRes.upstream?.data);
                const app = apps[0];
                if (!app) {
                    ElMessage.warning(`未找到 package=${pkg} os=${os} 的 Mintegral App`);
                    return;
                }
                this.mintegralAppId = String(app.app_id || app.appId || app.id || '').trim();
                this.mintegralAppKey = String(app.app_key || app.appKey || '').trim();
                const plRes = await admobGet(`/mintegral/apps/${this.mintegralAppId}/placements`);
                const placements = firstArray(plRes.upstream?.data);
                const wantType = mintegralAdType(this.formatX || this.groupAdFormat);
                this.mintegralPlacements = placements
                    .map((pl) => {
                        const id = String(pl.placement_id || pl.placementId || pl.id || '').trim();
                        const name = String(pl.placement_name || pl.placementName || pl.name || id).trim();
                        const adType = String(pl.ad_type || pl.adType || '').toLowerCase();
                        return {id, label: name && name !== id ? `${name} (${id})` : id, adType, raw: pl};
                    })
                    .filter((p) => p.id);
                const matched = this.mintegralPlacements.find((p) => p.adType === wantType);
                this.mintegralPlacementId = (matched || this.mintegralPlacements[0] || {}).id || '';
                await this.loadMintegralUnits();
            } catch (err) {
                showHttpError(err, '加载 Mintegral App/Placement 失败');
            }
        },
        async loadMintegralUnits() {
            this.remoteUnits = [];
            if (!this.mintegralAppId || !this.mintegralPlacementId) {
                return;
            }
            try {
                const result = await admobGet(
                    `/mintegral/apps/${this.mintegralAppId}/placements/${this.mintegralPlacementId}/units`,
                );
                this.remoteUnits = firstArray(result.upstream?.data);
            } catch (err) {
                showHttpError(err, '加载 Mintegral Units 失败');
                this.remoteUnits = [];
            }
        },
        async selectAdSource(id) {
            if (id && !isSupportedWaterfallSource(id)) {
                return;
            }
            if (id === this.selectedWaterfallId) {
                return;
            }
            const prev = this.selectedWaterfallId;
            if (
                prev &&
                hasUncommittedDraftForAdSource(this.draftRows, this.originalRows, prev)
            ) {
                try {
                    await ElMessageBox.confirm(
                        '切换广告源将丢弃当前源未提交的变更（新建层 / 底价修改 / 删除预览）。是否继续？',
                        '切换广告源',
                        {
                            type: 'warning',
                            confirmButtonText: '丢弃并切换',
                            cancelButtonText: '取消',
                        },
                    );
                } catch {
                    return;
                }
                this.draftRows = discardUncommittedDraftForAdSource(
                    this.draftRows,
                    this.originalRows,
                    prev,
                );
            }
            this.selectedWaterfallId = id;
            await this.loadPartnerRemoteOptions();
        },
        /** new=本地新建；line=上游已有行（不再支持下拉改绑普通广告单元） */
        rowMode(row) {
            if (row.isExisting || row.lineId) {
                return 'line';
            }
            return 'new';
        },
        adUnitIdPlaceholder(row) {
            const mode = this.rowMode(row);
            if (mode === 'new') {
                const sourceId = String(row.adSourceId || row.waterfallId || '').trim();
                const adapter = getWaterfallSource(sourceId);
                const hint = adapter?.displayNameHint || '对方单元';
                return `Apply 后串联创建 ${hint}`;
            }
            if (row.mapsToStandard) {
                return 'mapping→普通广告单元';
            }
            return '已有绑定 ID';
        },
        groupMatchesApp(group) {
            return mediationGroupMatchesApp(group, this.appAdUnitIds);
        },
        async loadAccounts() {
            this.loading = true;
            try {
                const result = await admobGet('/admob/accounts');
                const list = firstArray(result.upstream?.data);
                this.accounts = list.map((item) => {
                    const id = pickAdmobAccountId(item);
                    return {
                        id,
                        label: item.publisherId || item.name || id,
                        raw: item,
                    };
                }).filter((a) => a.id);
                if (this.accountId && !this.accounts.some((a) => a.id === this.accountId)) {
                    this.accountId = '';
                }
                if (!this.accountId && this.accounts.length === 1) {
                    this.accountId = this.accounts[0].id;
                    localStorage.setItem(ACCOUNT_STORAGE_KEY, this.accountId);
                }
                if (this.accountId) {
                    await this.reloadList();
                }
            } catch (err) {
                showHttpError(err, '加载 AdMob 账号失败');
            } finally {
                this.loading = false;
            }
        },
        async onAccountChange() {
            if (this.accountId) {
                localStorage.setItem(ACCOUNT_STORAGE_KEY, this.accountId);
            } else {
                localStorage.removeItem(ACCOUNT_STORAGE_KEY);
            }
            this.appId = '';
            this.apps = [];
            this.groups = [];
            this.appAdUnitIds = new Set();
            localStorage.removeItem(APP_STORAGE_KEY);
            if (this.accountId) {
                await this.reloadList();
            }
        },
        async onAppChange() {
            if (this.appId) {
                localStorage.setItem(APP_STORAGE_KEY, this.appId);
            } else {
                localStorage.removeItem(APP_STORAGE_KEY);
            }
            await this.refreshAppAdUnits();
        },
        async reloadList() {
            if (!this.accountId) {
                return;
            }
            this.loading = true;
            this.listHint = '';
            try {
                await Promise.all([this.loadApps(), this.loadGroupsRaw()]);
                await this.refreshAppAdUnits();
                await this.loadMetrics();
            } catch (err) {
                showHttpError(err, '加载中介组失败');
            } finally {
                this.loading = false;
            }
        },
        async loadApps() {
            const result = await admobGet(`/admob/accounts/${this.accountId}/apps`);
            const list = firstArray(result.upstream?.data);
            this.apps = list.map((app) => {
                const id = String(app.appId || app.app_id || resourceIdFromName(app.name) || '');
                const name =
                    app.displayName ||
                    app.manualAppInfo?.displayName ||
                    app.linkedAppInfo?.displayName ||
                    app.appStoreId ||
                    app.linkedAppInfo?.appStoreId ||
                    '';
                const label = name && name !== id ? `${name} (${id})` : id;
                return {appId: id, label, raw: app};
            }).filter((a) => a.appId);
            if (this.appId && !this.apps.some((a) => a.appId === this.appId)) {
                this.appId = '';
                localStorage.removeItem(APP_STORAGE_KEY);
            }
            if (!this.appId && this.apps.length === 1) {
                this.appId = this.apps[0].appId;
                localStorage.setItem(APP_STORAGE_KEY, this.appId);
            }
        },
        async refreshAppAdUnits() {
            if (!this.accountId || !this.appId) {
                this.appAdUnitIds = new Set();
                return;
            }
            try {
                const result = await admobGet(
                    `/admob/accounts/${this.accountId}/ad-units`,
                    {app_id: this.appId},
                );
                const units = firstArray(result.upstream?.data);
                this.appAdUnitIds = new Set(
                    units.map((u) => String(u.adUnitId || '')).filter(Boolean),
                );
            } catch (_err) {
                this.appAdUnitIds = new Set();
                this.listHint = (this.listHint ? this.listHint + ' ' : '') +
                    '按应用过滤时未能加载该应用广告单元，列表可能不准确。';
            }
        },
        async loadGroupsRaw() {
            const result = await admobGet(`/admob/accounts/${this.accountId}/mediation-groups`);
            const list = firstArray(result.upstream?.data);
            this.groups = list.map((g) => {
                const id = String(g.mediationGroupId || resourceIdFromName(g.name) || g.id || '');
                const metrics = this.metricsByGroup[id] || {};
                return {
                    ...g,
                    mediationGroupId: id,
                    revenue: metrics.revenue ?? null,
                    impressions: metrics.impressions ?? null,
                };
            });
        },
        async loadMetrics() {
            if (!this.accountId) {
                return;
            }
            try {
                const range = recentDateRange(7);
                const result = await admobReport(
                    `/admob/accounts/${this.accountId}/reports/mediation`,
                    {
                        dateRange: range,
                        dimensions: ['MEDIATION_GROUP'],
                        metrics: ['IMPRESSIONS', 'ESTIMATED_EARNINGS'],
                        maxReportRows: 10000,
                    },
                );
                const data = result.upstream?.data || {};
                this.metricsByGroup = parseMediationReportByGroup(data);
                let matched = 0;
                this.groups = this.groups.map((g) => {
                    const metrics = lookupGroupMetrics(this.metricsByGroup, g);
                    if (metrics.revenue != null || metrics.impressions != null) {
                        matched += 1;
                    }
                    return {
                        ...g,
                        revenue: metrics.revenue,
                        impressions: metrics.impressions,
                    };
                });
                const rowCount = Number(data.rowCount ?? (data.rows || []).length ?? 0);
                this.listHint =
                    `近 7 日 mediation report：上游 ${rowCount} 行，已匹配 ${matched}/${this.groups.length} 个中介组。` +
                    `状态默认「启用」。切换应用后按该应用广告单元过滤。`;
            } catch (err) {
                this.listHint = `报表未加载：${err?.message || err}；收入/展示显示为 -。`;
                this.groups = this.groups.map((g) => ({
                    ...g,
                    revenue: null,
                    impressions: null,
                }));
            }
        },
        async openEditor(row) {
            this.editingGroup = row;
            this.editorOpen = true;
            this.draftRows = [];
            this.originalRows = [];
            try {
                await this.reloadEditor({refreshCache: false});
            } catch (err) {
                showHttpError(err, '加载瀑布流失败');
                this.editorOpen = false;
            }
        },
        async reloadEditor(opts = {}) {
            const row = this.editingGroup;
            if (!row) {
                return;
            }
            const refresh = opts.refreshCache ? true : undefined;
            const cacheQuery = refresh ? {refresh_cache: 'true'} : {};
            this.editorLoading = true;
            try {
                const gid = row.mediationGroupId;
                const [detailRes, waterfallRes, unitsRes, sourcesRes, opmcRes] = await Promise.all([
                    admobGet(`/admob/accounts/${this.accountId}/mediation-groups/${gid}`, cacheQuery),
                    admobGet(`/admob/accounts/${this.accountId}/mediation-groups/${gid}/waterfall`, cacheQuery),
                    admobGet(`/admob/accounts/${this.accountId}/ad-units`, cacheQuery),
                    admobGet(`/admob/accounts/${this.accountId}/ad-sources`, cacheQuery).catch(() => null),
                    admobGet(
                        `/admob/accounts/${this.accountId}/admob-network-waterfall-ad-units`,
                        cacheQuery,
                    ).catch(() => {
                        ElMessage.warning('加载 OPMC 列表失败，选已有 OPMC 可能为空');
                        return null;
                    }),
                ]);

                const detail = detailRes.upstream?.data || {};
                this.mediationAbExperimentState = normalizeExperimentState(
                    detail.mediationAbExperimentState ?? detail.mediation_ab_experiment_state,
                );
                const targeting = detail.targeting || {};
                this.originalTargetingAdUnitIds = [...(targeting.adUnitIds || targeting.ad_unit_ids || [])];
                this.draftTargetingAdUnitIds = [...this.originalTargetingAdUnitIds];
                this.groupAdFormat = String(
                    targeting.format || targeting.adFormat || targeting.ad_format || detail.adFormat || '',
                ).toUpperCase();

                this.allAdUnits = firstArray(unitsRes.upstream?.data);
                this.allOpmcUnits = opmcRes
                    ? firstArray(opmcRes.upstream?.data)
                    : [];
                const firstTarget = this.draftTargetingAdUnitIds[0];
                const matched = this.allAdUnits.find((u) => u.adUnitId === firstTarget);
                this.groupAppId = this.appId || matched?.appId || '';

                if (!this.groupAdFormat && matched?.adFormat) {
                    this.groupAdFormat = String(matched.adFormat).toUpperCase();
                }

                const lines = resolveMediationGroupLines(waterfallRes.upstream?.data);
                this.upstreamMediationLines = Array.isArray(lines) ? lines.map((l) => ({...l})) : [];
                let draft = linesToDraftRows(lines, this.originalTargetingAdUnitIds);

                const standardResourceId = (() => {
                    const targeting0 = String(this.draftTargetingAdUnitIds?.[0] || '').trim();
                    if (!targeting0) {
                        return '';
                    }
                    const unit = this.allAdUnits.find((u) => String(u.adUnitId) === targeting0);
                    return unit ? adUnitResourceIdFromName(unit.name) : '';
                })();
                if (standardResourceId) {
                    try {
                        const mapRes = await admobGet(
                            `/admob/accounts/${this.accountId}/ad-units/${standardResourceId}/mappings`,
                            cacheQuery,
                        );
                        const indexed = indexOpmcIdByMappingName(
                            firstArray(mapRes.upstream?.data),
                        );
                        draft = withBoundOpmcIds(draft, lines, indexed);
                    } catch {
                        // Mapping list is best-effort; displayName fallback still applies.
                    }
                }
                const opmcForName = (this.allOpmcUnits || [])
                    .map((unit) => ({
                        id: this.opmcUnitId(unit),
                        displayName: String(unit?.displayName || '').trim(),
                    }))
                    .filter((u) => u.id);
                draft = fillBoundOpmcIdsByDisplayName(draft, opmcForName);

                this.originalRows = draft;
                this.draftRows = draft.map((r) => ({...r}));

                const sources = sourcesRes ? firstArray(sourcesRes.upstream?.data) : [];
                const nameById = {};
                for (const s of sources) {
                    const sid = String(s.adSourceId || s.id || '');
                    if (sid) {
                        nameById[sid] = s.title || s.displayName || s.name || sid;
                    }
                }
                this.adSourceNameById = nameById;

                const catalog = sources.map((s) => {
                    const id = String(s.adSourceId || s.id || '');
                    return {
                        id,
                        name: s.title || s.displayName || s.name || id,
                    };
                }).filter((s) => s.id);
                this.adSourceCatalog = catalog;

                const admobWaterfall = sources.find((s) =>
                    /waterfall/i.test(String(s.title || s.displayName || s.name || '')),
                );
                const admobSource = sources.find((s) =>
                    /admob/i.test(String(s.title || s.displayName || s.name || '')),
                );
                this.adSourceId = String(
                    (admobWaterfall || admobSource || sources[0])?.adSourceId ||
                    (admobWaterfall || admobSource || sources[0])?.id ||
                    '',
                );

                // Keep partner toolbar source when filter is exactly one platform
                this.selectedPlatformIds = defaultSelectedPlatformIds(listSupportedSourceIds());
                this.selectedWaterfallId = pickWaterfallIdAfterReload(
                    this.waterfallPanelOptions,
                    this.selectedPlatformIds.length === 1
                        ? this.selectedPlatformIds[0]
                        : this.selectedWaterfallId,
                    ADMOB_NETWORK_WATERFALL_SOURCE_ID,
                );
                if (this.selectedPlatformIds.length === 1) {
                    this.selectedWaterfallId = this.selectedPlatformIds[0];
                } else if (!this.selectedPlatformIds.includes(this.selectedWaterfallId)) {
                    this.selectedWaterfallId = '';
                }
                await this.loadPartnerRemoteOptions();
            } catch (err) {
                if (opts.refreshCache) {
                    showHttpError(err, '刷新瀑布流失败');
                }
                throw err;
            } finally {
                this.editorLoading = false;
            }
        },
        resetEditor() {
            this.editingGroup = null;
            this.draftRows = [];
            this.originalRows = [];
            this.upstreamMediationLines = [];
            this.mediationAbExperimentState = 'UNKNOWN';
            this.abCreateOpen = false;
            this.abStopOpen = false;
            this.abFloorOverrides = [];
            this.abCreateError = '';
            this.abStopError = '';
            this.allOpmcUnits = [];
            this.adSourceCatalog = [];
            this.selectedWaterfallId = '';
            this.selectedPlatformIds = [];
            this.targetingEditorOpen = false;
            this.importDialogOpen = false;
            this.importSourceGroupId = '';
            this.importLoading = false;
            this.inmobiAccounts = [];
            this.inmobiApps = [];
            this.pangleApps = [];
            this.vungleApps = [];
            this.remoteUnits = [];
            this.mintegralAppKey = '';
            this.mintegralAppId = '';
            this.mintegralPlacements = [];
            this.mintegralPlacementId = '';
            this.confirmRan = false;
            this.confirmResults = [];
            this.applyRan = false;
            this.applyResults = [];
            this.applyArtifacts = {};
        },
        resolveStandardAdUnitResourceId() {
            const targeting0 = String(this.draftTargetingAdUnitIds?.[0] || '').trim();
            if (!targeting0) {
                return '';
            }
            const unit = this.allAdUnits.find((u) => String(u.adUnitId) === targeting0);
            return unit ? adUnitResourceIdFromName(unit.name) : '';
        },
        addRowAfter(index) {
            let waterfallId =
                this.selectedPlatformIds.length === 1
                    ? this.selectedPlatformIds[0]
                    : '';
            const selected = (this.adSourceOptions || []).find((o) => o.id === waterfallId);
            const mode = selected?.modeHint === 'bidding' ? 'LIVE' : 'MANUAL';
            // MANUAL floors under classic AdMob Network must use Waterfall source for OPMC chain.
            if (
                mode === 'MANUAL' &&
                (waterfallId === ADMOB_NETWORK_SOURCE_ID ||
                    waterfallId === ADMOB_NETWORK_WATERFALL_SOURCE_ID)
            ) {
                waterfallId = ADMOB_NETWORK_WATERFALL_SOURCE_ID;
            }
            const row = newDraftRow(this.draftRows.length, waterfallId, mode);
            row.opmcBindMode = 'create';
            row.selectedOpmcId = '';
            if (!waterfallId) {
                row.waterfallId = '';
                row.adSourceId = '';
            }
            if (index < 0) {
                this.draftRows.push(row);
                return;
            }
            const visible = this.activeDraftRows;
            const target = visible[index];
            if (!target) {
                this.draftRows.push(row);
                return;
            }
            // Prefer same platform as the row we insert after when filter is multi-select
            if (!waterfallId) {
                const siblingId = String(target.adSourceId || target.waterfallId || '').trim();
                if (siblingId) {
                    this.onRowPlatformChange(row, siblingId);
                }
            }
            const realIndex = this.draftRows.findIndex((r) => r.key === target.key);
            this.draftRows.splice(realIndex + 1, 0, row);
        },
        markDeleted(row) {
            // 新建：直接移掉
            if (this.rowMode(row) === 'new' || (!row.isExisting && !row.lineId)) {
                this.draftRows = this.draftRows.filter((r) => r.key !== row.key);
                return;
            }
            // 已有行：置灰，恢复成删除前的原文，不可再改
            const orig = this.originalRows.find(
                (r) => r.key === row.key || r.lineId === row.lineId,
            );
            if (orig) {
                row.displayName = orig.displayName;
                row.adUnitId = orig.adUnitId;
                row.selectedAdUnitId = orig.selectedAdUnitId;
                row.floorPrice = orig.floorPrice;
                row.mapsToStandard = orig.mapsToStandard;
                row.lockedExceptFloor = orig.lockedExceptFloor;
            }
            row.deleted = true;
        },
        restoreRow(row) {
            row.deleted = false;
        },
        onTargetingChange() {
            const targeting = new Set(this.draftTargetingAdUnitIds.map(String));
            for (const row of this.draftRows) {
                if (!row.deleted && row.adUnitId && targeting.has(row.adUnitId)) {
                    row.mapsToStandard = true;
                } else if (row.adUnitId) {
                    row.mapsToStandard = targeting.has(row.adUnitId);
                }
            }
            const matched = this.allAdUnits.find(
                (u) => u.adUnitId === this.draftTargetingAdUnitIds[0],
            );
            if (matched?.appId) {
                this.groupAppId = matched.appId;
            }
            if (matched?.adFormat) {
                this.groupAdFormat = String(matched.adFormat).toUpperCase();
            }
        },
        openConfirm() {
            if (this.editorWriteLocked) {
                ElMessage.warning('运行中的 AB 测试期间不能修改瀑布流，请先停止实验');
                return;
            }
            const activeNew = (this.draftRows || []).filter(
                (r) => !r.deleted && this.rowMode(r) === 'new',
            );
            if (activeNew.some((r) => !String(r.adSourceId || r.waterfallId || '').trim())) {
                ElMessage.error('新建层必须选择平台');
                return;
            }
            if (activeNew.some((r) => !String(r.displayName || '').trim())) {
                ElMessage.error('新建层必须填写层名称');
                return;
            }
            if (activeNew.some((r) => r.floorPrice == null || Number.isNaN(Number(r.floorPrice)))) {
                ElMessage.error('新建层必须填写底价');
                return;
            }
            if (this.hasExistingOpmcLayerName()) {
                ElMessage.error('名称已存在');
                return;
            }
            if (hasDuplicateDisplayNames(this.draftRows)) {
                ElMessage.error('层名称不能重复');
                return;
            }
            if (hasDuplicateFloorPrices(this.draftRows)) {
                ElMessage.error('底价不能重复');
                return;
            }
            const formatNotes = collectFormatMismatchNotes({
                targetingAdUnitIds: this.draftTargetingAdUnitIds,
                waterfallAdUnitIds: [], // OPMC 新建不再绑定普通广告单元 ID
                adUnits: this.allAdUnits,
                requiredFormat: this.formatX || this.groupAdFormat,
            });

            this.confirmPlan = buildConfirmPlan({
                accountId: this.accountId,
                mediationGroupId: this.editingGroup.mediationGroupId,
                appId: this.groupAppId,
                adFormat: this.formatX || this.groupAdFormat || 'BANNER',
                adTypes: (this.formatX || this.groupAdFormat) === 'REWARDED' ? ['VIDEO'] : ['RICH_MEDIA'],
                adSourceId: this.adSourceId,
                originalRows: this.originalRows,
                draftRows: this.draftRows,
                originalTargetingAdUnitIds: this.originalTargetingAdUnitIds,
                draftTargetingAdUnitIds: this.draftTargetingAdUnitIds,
                standardAdUnitResourceId: this.resolveStandardAdUnitResourceId() || undefined,
                platform: resolveAppPlatform(this.apps, this.groupAppId || this.appId),
                // account/app come from each row (额外配置); keep globals unset
                packageName: this.resolveAppPackageName() || undefined,
                applovinSdkKey: this.applovinSdkKey || undefined,
                mintegralAppKey: this.mintegralAppKey || undefined,
                mintegralAppId: this.mintegralAppId || undefined,
                mintegralPlacementId: this.mintegralPlacementId || undefined,
            });
            const deduped = [];
            const seen = new Set();
            for (const note of [
                ...formatNotes.map((message, i) => ({id: `format-${i}`, message})),
                ...this.confirmPlan.missingApis,
                // DO NOT inject no-delivery-type
            ]) {
                if (seen.has(note.message)) {
                    continue;
                }
                seen.add(note.message);
                deduped.push(note);
            }
            this.confirmPlan.missingApis = deduped;
            this.confirmResults = [];
            this.confirmRan = false;
            this.applyResults = [];
            this.applyRan = false;
            this.applyArtifacts = {};
            this.confirmOpen = true;
        },
        async runDryRuns() {
            const pending = pendingConfirmSteps(
                this.confirmPlan.steps,
                this.applyResults,
            );
            if (!pending.length) {
                ElMessage.info('没有待执行的步骤');
                return;
            }
            this.confirming = true;
            try {
                const updates = [];
                for (const step of pending) {
                    if (step.dependsOn) {
                        updates.push({
                            id: step.id,
                            ok: true,
                            dryRunId: '',
                            planHash: '',
                            message: 'Apply 时串联校验',
                        });
                        continue;
                    }
                    try {
                        const result = await admobDryRun(
                            step.method,
                            step.path,
                            step.change,
                            step.dryRunExtras || {},
                        );
                        const data = result.upstream?.data || {};
                        updates.push({
                            id: step.id,
                            ok: true,
                            message: 'ok',
                            dryRunId: String(data.dry_run_id || ''),
                            planHash: String(data.plan_hash || ''),
                        });
                    } catch (err) {
                        updates.push({
                            id: step.id,
                            ok: false,
                            message: err?.message || String(err),
                            dryRunId: '',
                            planHash: '',
                        });
                    }
                }
                this.confirmResults = mergeConfirmResults(this.confirmResults, updates);
                this.confirmRan = true;
                const failed = updates.filter((r) => !r.ok).length;
                const missingPlan = updates.filter((r) => {
                    if (!r.ok || (r.dryRunId && r.planHash)) {
                        return false;
                    }
                    const step = pending.find((s) => s.id === r.id);
                    return !step?.dependsOn;
                }).length;
                if (failed) {
                    const first = updates.find((r) => !r.ok);
                    const detail = String(first?.message || '').trim();
                    ElMessage.error(
                        detail
                            ? `dry-run 完成：${failed} 步失败 — ${summarizeErrorMessage(detail, 200)}`
                            : `dry-run 完成：${failed} 步失败`,
                    );
                } else if (missingPlan) {
                    ElMessage.warning(
                        'dry-run 通过，但未返回 dry_run_id/plan_hash，无法 Apply',
                    );
                } else {
                    ElMessage.success('待执行步骤 dry-run 通过，可 Apply');
                }
            } finally {
                this.confirming = false;
            }
        },
        storeApplyArtifact(stepId, patch) {
            this.applyArtifacts = {
                ...this.applyArtifacts,
                [stepId]: {...(this.applyArtifacts[stepId] || {}), ...patch},
            };
        },
        async applyDependentStep(step) {
            if (step.kind === 'create-mapping') {
                const resourceId = String(
                    step.standardAdUnitResourceId || this.resolveStandardAdUnitResourceId() || '',
                ).trim();
                if (!resourceId) {
                    throw new Error('缺少标准广告单元 resource id，无法创建 mapping');
                }
                const path = `/admob/accounts/${this.accountId}/ad-units/${resourceId}/mappings`;
                const row = this.draftRows.find((r) => r.key === step.rowKey);
                let change = step.change;
                if (!mappingChangeHasConfigs(change) && !mappingChangeHasOpmcConfig(change)) {
                    const dep = this.applyArtifacts[step.dependsOn] || {};
                    const remoteId = String(dep.remoteUnitId || dep.opmcId || '').trim();
                    if (!remoteId) {
                        throw new Error('缺少前序对方单元 id，无法创建 mapping');
                    }
                    const platform = resolveAppPlatform(this.apps, this.groupAppId || this.appId);
                    const format = this.formatX || this.groupAdFormat || 'BANNER';
                    const displayName = row?.displayName || undefined;
                    const sourceId = String(row?.adSourceId || row?.waterfallId || '').trim();
                    if (sourceId === INMOBI_SOURCE_ID) {
                        const accountId = String(row?.thirdPartyAccountId || '').trim();
                        if (!accountId) {
                            throw new Error('缺少 InMobi 账号，无法创建 mapping');
                        }
                        change = buildInmobiMappingChange({
                            accountId,
                            placementId: remoteId,
                            displayName,
                            platform,
                            format,
                        });
                    } else if (sourceId === APPLOVIN_SOURCE_ID) {
                        change = buildApplovinMappingChange({
                            sdkKey: this.applovinSdkKey,
                            packageName: this.resolveAppPackageName(),
                            zoneId: remoteId,
                            displayName,
                            platform,
                            format,
                        });
                    } else if (sourceId === MINTEGRAL_SOURCE_ID) {
                        change = buildMintegralMappingChange({
                            appKey: this.mintegralAppKey,
                            appId: this.mintegralAppId,
                            placementId: this.mintegralPlacementId,
                            unitId: remoteId,
                            displayName,
                            platform,
                            format,
                        });
                    } else if (sourceId === PANGLE_SOURCE_ID) {
                        const appId = String(row?.partnerAppId || '').trim();
                        if (!appId) {
                            throw new Error('缺少 Pangle App（额外配置），无法创建 mapping');
                        }
                        change = buildPangleMappingChange({
                            appId,
                            placementId: remoteId,
                            displayName,
                            platform,
                            format,
                        });
                    } else if (sourceId === VUNGLE_SOURCE_ID) {
                        const appId = String(row?.partnerAppId || '').trim();
                        if (!appId) {
                            throw new Error('缺少 Vungle App（额外配置），无法创建 mapping');
                        }
                        change = buildVungleMappingChange({
                            appId,
                            placementId: remoteId,
                            displayName,
                            platform,
                            format,
                        });
                    } else {
                        change = buildOpmcMappingChange({
                            opmcId: remoteId,
                            displayName,
                            platform,
                            format,
                        });
                    }
                }
                const dryResult = await admobDryRun(step.method, path, change);
                const dryData = dryResult.upstream?.data || {};
                const dryRunId = String(dryData.dry_run_id || '');
                const planHash = String(dryData.plan_hash || '');
                if (!dryRunId || !planHash) {
                    throw new Error('mapping dry-run 未返回 dry_run_id / plan_hash');
                }
                const applyResult = await admobApply(step.method, path, dryRunId, planHash);
                const mappingName = parseMappingNameFromApplyData(applyResult.upstream?.data);
                if (!mappingName) {
                    throw new Error('mapping Apply 成功但未解析到 mapping.name');
                }
                this.storeApplyArtifact(step.id, {mappingName});
                return;
            }
            if (step.kind === 'add-waterfall-line') {
                const dep = this.applyArtifacts[step.dependsOn] || {};
                const mappingName = String(dep.mappingName || '').trim();
                if (!mappingName) {
                    throw new Error('缺少前序 mapping.name，无法绑定瀑布流行');
                }
                const standardAdUnitId = String(step.standardAdUnitId || '').trim();
                if (!standardAdUnitId) {
                    throw new Error('缺少标准广告单元 id，无法绑定瀑布流行');
                }
                const lines = step.change?.mediationGroupLines || {};
                const tempLineId = Object.keys(lines)[0] || '';
                const line = lines[tempLineId] || {};
                const row = this.draftRows.find((r) => r.key === step.rowKey);
                const cpmMicros =
                    String(line.cpmMicros || '').trim() ||
                    dollarsToCpmMicros(row?.floorPrice) ||
                    '';
                if (!tempLineId || !cpmMicros) {
                    throw new Error('瀑布流行缺少临时 line id 或 cpmMicros');
                }
                const change = buildWaterfallLineChange({
                    tempLineId,
                    displayName: line.displayName || row?.displayName || 'waterfall_line',
                    standardAdUnitId,
                    mappingName,
                    cpmMicros,
                    adSourceId: line.adSourceId || row?.adSourceId,
                });
                const dryResult = await admobDryRun(step.method, step.path, change);
                const dryData = dryResult.upstream?.data || {};
                const dryRunId = String(dryData.dry_run_id || '');
                const planHash = String(dryData.plan_hash || '');
                if (!dryRunId || !planHash) {
                    throw new Error('瀑布流行 dry-run 未返回 dry_run_id / plan_hash');
                }
                await admobApply(step.method, step.path, dryRunId, planHash);
                return;
            }
            throw new Error(`未知的依赖步骤 kind: ${step.kind || '(empty)'}`);
        },
        async runApplies() {
            if (!this.canApply) {
                ElMessage.warning('请先完成待执行步骤的 dry-run');
                return;
            }
            const pending = pendingConfirmSteps(
                this.confirmPlan.steps,
                this.applyResults,
            );
            this.applying = true;
            try {
                for (const step of pending) {
                    try {
                        if (step.dependsOn) {
                            await this.applyDependentStep(step);
                        } else {
                            const dry = this.confirmResultsMap[step.id];
                            if (!dry?.dryRunId || !dry?.planHash) {
                                throw new Error('缺少 dry_run_id / plan_hash');
                            }
                            const applyResult = await admobApply(
                                step.method,
                                step.path,
                                dry.dryRunId,
                                dry.planHash,
                            );
                            if (step.kind === 'create-opmc' || step.kind === 'create-remote') {
                                const data = applyResult.upstream?.data;
                                let remoteUnitId = '';
                                if (step.kind === 'create-opmc') {
                                    remoteUnitId = parseOpmcIdFromApplyData(data);
                                } else if (String(step.path || '').startsWith('/inmobi/')) {
                                    remoteUnitId = parseInmobiPlacementIdFromApplyData(data);
                                } else if (String(step.path || '').startsWith('/max/')) {
                                    remoteUnitId = parseMaxAdUnitIdFromApplyData(data);
                                } else if (String(step.path || '').startsWith('/mintegral/')) {
                                    remoteUnitId = parseMintegralUnitIdFromApplyData(data);
                                } else if (String(step.path || '').startsWith('/pangle/')) {
                                    remoteUnitId = parsePanglePlacementIdFromApplyData(data);
                                } else if (String(step.path || '').startsWith('/vungle/')) {
                                    remoteUnitId = parseVunglePlacementIdFromApplyData(data);
                                }
                                if (!remoteUnitId) {
                                    throw new Error(
                                        `${step.kind} Apply 成功但未解析到对方单元 id`,
                                    );
                                }
                                this.storeApplyArtifact(step.id, {
                                    opmcId: remoteUnitId,
                                    remoteUnitId,
                                });
                            }
                            if (step.kind === 'create-mapping') {
                                const mappingName = parseMappingNameFromApplyData(
                                    applyResult.upstream?.data,
                                );
                                if (!mappingName) {
                                    throw new Error('mapping Apply 成功但未解析到 mapping.name');
                                }
                                this.storeApplyArtifact(step.id, {mappingName});
                            }
                        }
                        this.applyResults = mergeConfirmResults(this.applyResults, [
                            {id: step.id, ok: true, message: 'ok'},
                        ]);
                    } catch (err) {
                        this.applyResults = mergeConfirmResults(this.applyResults, [
                            {
                                id: step.id,
                                ok: false,
                                message: err?.message || String(err),
                            },
                        ]);
                        break;
                    }
                }
                this.applyRan = true;
                const allDone = pendingConfirmSteps(
                    this.confirmPlan.steps,
                    this.applyResults,
                ).length === 0;
                const failed = this.applyResults.filter((r) => !r.ok).length;
                const done = this.applyResults.filter((r) => r.ok).length;
                if (allDone) {
                    try {
                        await this.reloadEditor({refreshCache: true});
                        await this.reloadList();
                        ElMessage.success('全部步骤已 Apply');
                        this._skipNextConfirmClosedRefresh = true;
                        this.confirmOpen = false;
                    } catch {
                        // reloadEditor toasts on refresh failure; keep Confirm open so close can retry
                    }
                } else if (failed) {
                    const firstFail = this.applyResults.find((r) => !r.ok);
                    const detail = String(firstFail?.message || '').trim();
                    ElMessage.error(
                        detail
                            ? `Apply 中断：成功 ${done}，失败 ${failed} — ${summarizeErrorMessage(detail, 200)}`
                            : `Apply 中断：成功 ${done}，失败 ${failed}`,
                    );
                }
            } finally {
                this.applying = false;
            }
        },
        async onConfirmClosed() {
            if (this._skipNextConfirmClosedRefresh) {
                this._skipNextConfirmClosedRefresh = false;
                return;
            }
            const anyApplied = this.applyResults.some((r) => r.ok);
            if (!anyApplied || !this.editingGroup) {
                return;
            }
            try {
                await this.reloadEditor({refreshCache: true});
                await this.reloadList();
            } catch {
                // reloadEditor already toasted on refresh failure
            }
        },
        resetCreateForm() {
            this.createAccountId = '';
            this.createAppId = '';
            this.createApps = [];
            this.createAppAdUnitIds = new Set();
            this.createGroupsForNames = [];
            this.createAppsLoading = false;
            this.createAdUnits = [];
            this.createAdapters = [];
            this.createFormatsError = '';
            this.createFormatsLoading = false;
            this.createForm = {
                displayName: '',
                platform: '',
                format: '',
                adUnitId: '',
                state: 'ENABLED',
            };
            this.createDryRunId = '';
            this.createPlanHash = '';
            this.createError = '';
            this.createDryRunning = false;
            this.createApplying = false;
            this.createUnitOpen = false;
            this.resetCreateUnitForm();
        },
        resetCreateUnitForm() {
            this.createUnitForm = {
                displayName: '',
                unitAmount: '10',
                unitType: 'coin',
            };
            this.createUnitDryRunId = '';
            this.createUnitPlanHash = '';
            this.createUnitError = '';
            this.createUnitDryRunning = false;
            this.createUnitApplying = false;
        },
        openCreateUnit() {
            if (!this.canOpenCreateUnit) {
                ElMessage.warning('请先选择应用并选定广告格式');
                return;
            }
            this.resetCreateUnitForm();
            this.createUnitOpen = true;
        },
        buildCreateUnitChange() {
            return buildCreateAdUnitChange({
                appId: this.createAppId,
                displayName: this.createUnitForm.displayName,
                adFormat: this.createForm.format,
                unitAmount: this.createUnitForm.unitAmount,
                unitType: this.createUnitForm.unitType,
            });
        },
        async dryRunCreateUnit() {
            if (this.createUnitNameTaken) {
                ElMessage.error('名称已存在');
                return;
            }
            if (!this.canSubmitCreateUnit) {
                ElMessage.warning('请填写名称（激励格式还需奖励数量与类型）');
                return;
            }
            this.createUnitDryRunning = true;
            this.createUnitError = '';
            this.createUnitDryRunId = '';
            this.createUnitPlanHash = '';
            try {
                const path = `/admob/accounts/${this.createAccountId}/ad-units`;
                const result = await admobDryRun('POST', path, this.buildCreateUnitChange());
                const data = result.upstream?.data || {};
                this.createUnitDryRunId = String(data.dry_run_id || '');
                this.createUnitPlanHash = String(data.plan_hash || '');
                if (!this.createUnitDryRunId || !this.createUnitPlanHash) {
                    throw new Error('dry-run 未返回 dry_run_id / plan_hash');
                }
                ElMessage.success('dry-run 通过，可 Apply');
            } catch (err) {
                this.createUnitError = err?.message || String(err);
                ElMessage.error(this.createUnitError);
            } finally {
                this.createUnitDryRunning = false;
            }
        },
        async applyCreateUnit() {
            if (!this.createUnitDryRunId || !this.createUnitPlanHash) {
                ElMessage.warning('请先执行 dry-run');
                return;
            }
            this.createUnitApplying = true;
            this.createUnitError = '';
            const intendedName = String(this.createUnitForm.displayName || '').trim();
            const intendedFormat = this.createForm.format;
            try {
                const path = `/admob/accounts/${this.createAccountId}/ad-units`;
                const result = await admobApply(
                    'POST',
                    path,
                    this.createUnitDryRunId,
                    this.createUnitPlanHash,
                );
                const applyData = result.upstream?.data || {};
                this.createDryRunId = '';
                this.createPlanHash = '';
                this.createUnitDryRunId = '';
                this.createUnitPlanHash = '';
                this.createUnitOpen = false;
                try {
                    const listRes = await admobGet(
                        `/admob/accounts/${this.createAccountId}/ad-units`,
                        {
                            app_id: this.createAppId,
                            format: this.createForm.format,
                        },
                    );
                    this.createAdUnits = firstArray(listRes.upstream?.data);
                } catch (_refreshErr) {
                    // Apply already succeeded; pickAdUnitIdAfterCreate may still use applyData.
                }
                const newId = pickAdUnitIdAfterCreate({
                    applyData,
                    units: this.createAdUnits,
                    displayName: intendedName,
                    adFormat: intendedFormat,
                    appId: this.createAppId,
                });
                if (newId) {
                    this.createForm.adUnitId = newId;
                    ElMessage.success('广告单元已创建并选中');
                } else {
                    ElMessage.warning('广告单元已创建，但未能自动选中，请从下拉手动选择');
                }
            } catch (err) {
                this.createUnitError = err?.message || String(err);
                ElMessage.error(this.createUnitError);
            } finally {
                this.createUnitApplying = false;
            }
        },
        applyCreateFormatsForPlatform() {
            const formats = collectFormatsFromAdapters(
                this.createAdapters,
                this.createForm.platform,
            );
            const next = pickDefaultFormat(formats, this.createForm.format);
            if (next !== this.createForm.format) {
                this.createForm.format = next;
                this.createForm.adUnitId = '';
            }
            const platformNoFormatsMsg =
                '无法从 AdMob adapters 获取广告格式（当前平台无可用格式）';
            if (formats.length > 0) {
                if (this.createFormatsError === platformNoFormatsMsg) {
                    this.createFormatsError = '';
                }
            } else if (
                this.createAdapters.length > 0 &&
                this.createFormatsError !== '无法从 AdMob adapters 获取广告格式' &&
                this.createFormatsError !== '无法从 AdMob adapters 获取广告格式（空列表）'
            ) {
                this.createFormatsError = platformNoFormatsMsg;
            }
        },
        clearCreateAppDownstream() {
            this.createAppId = '';
            this.createAppAdUnitIds = new Set();
            this.createGroupsForNames = [];
            this.createAdUnits = [];
            this.createAdapters = [];
            this.createFormatsError = '';
            this.createForm.platform = '';
            this.createForm.format = '';
            this.createForm.adUnitId = '';
            this.createDryRunId = '';
            this.createPlanHash = '';
            this.createError = '';
        },
        async loadCreateApps() {
            if (!this.createAccountId) {
                this.createApps = [];
                return;
            }
            this.createAppsLoading = true;
            try {
                const result = await admobGet(`/admob/accounts/${this.createAccountId}/apps`);
                const list = firstArray(result.upstream?.data);
                this.createApps = list
                    .map((app) => {
                        const id = String(
                            app.appId || app.app_id || resourceIdFromName(app.name) || '',
                        );
                        const name =
                            app.displayName ||
                            app.manualAppInfo?.displayName ||
                            app.linkedAppInfo?.displayName ||
                            app.appStoreId ||
                            app.linkedAppInfo?.appStoreId ||
                            '';
                        const label = name && name !== id ? `${name} (${id})` : id;
                        return {appId: id, label, raw: app};
                    })
                    .filter((a) => a.appId);
                if (this.createAppId && !this.createApps.some((a) => a.appId === this.createAppId)) {
                    this.clearCreateAppDownstream();
                }
                if (!this.createAppId && this.createApps.length === 1) {
                    this.createAppId = this.createApps[0].appId;
                    await this.onCreateAppChange();
                }
            } catch (err) {
                showHttpError(err, '加载应用失败');
                this.createApps = [];
                this.clearCreateAppDownstream();
            } finally {
                this.createAppsLoading = false;
            }
        },
        async onCreateAccountChange() {
            this.clearCreateAppDownstream();
            this.createApps = [];
            await this.loadCreateApps();
        },
        async onCreateAppChange() {
            this.createForm.format = '';
            this.createForm.adUnitId = '';
            this.createDryRunId = '';
            this.createPlanHash = '';
            this.createError = '';
            this.createAdUnits = [];
            this.createAdapters = [];
            this.createFormatsError = '';
            this.createAppAdUnitIds = new Set();
            this.createGroupsForNames = [];
            this.createForm.platform = '';

            if (!this.createAccountId || !this.createAppId) {
                return;
            }

            this.createForm.platform = resolveAppPlatform(this.createApps, this.createAppId);
            this.createLoading = true;
            this.createFormatsLoading = true;
            try {
                const unitsPromise = admobGet(
                    `/admob/accounts/${this.createAccountId}/ad-units`,
                    {app_id: this.createAppId},
                );
                const groupsPromise = admobGet(
                    `/admob/accounts/${this.createAccountId}/mediation-groups`,
                );

                let adapters = [];
                try {
                    const sourcesRes = await admobGet(
                        `/admob/accounts/${this.createAccountId}/ad-sources`,
                    );
                    const sources = firstArray(sourcesRes.upstream?.data);
                    const network =
                        sources.find(
                            (s) =>
                                String(s.adSourceId || s.id || '') === ADMOB_NETWORK_SOURCE_ID,
                        ) ||
                        sources.find((s) =>
                            String(s.title || s.name || '')
                                .toLowerCase()
                                .includes('admob network'),
                        );
                    const sourceId = String(
                        network?.adSourceId ||
                            network?.id ||
                            resourceIdFromName(network?.name) ||
                            ADMOB_NETWORK_SOURCE_ID,
                    );
                    const adaptersRes = await admobGet(
                        `/admob/accounts/${this.createAccountId}/ad-sources/${sourceId}/adapters`,
                    );
                    adapters = firstArray(adaptersRes.upstream?.data);
                    if (!adapters.length) {
                        this.createFormatsError =
                            '无法从 AdMob adapters 获取广告格式（空列表）';
                    }
                } catch (err) {
                    this.createFormatsError = '无法从 AdMob adapters 获取广告格式';
                    showHttpError(err, '加载广告格式失败');
                    adapters = [];
                }
                this.createAdapters = adapters;
                this.applyCreateFormatsForPlatform();

                const [unitsResult, groupsResult] = await Promise.all([
                    unitsPromise,
                    groupsPromise,
                ]);
                const units = firstArray(unitsResult.upstream?.data);
                this.createAdUnits = units;
                this.createAppAdUnitIds = new Set(
                    units.map((u) => String(u.adUnitId || '')).filter(Boolean),
                );
                const rawGroups = firstArray(groupsResult.upstream?.data);
                this.createGroupsForNames = rawGroups.filter((g) =>
                    mediationGroupMatchesApp(g, this.createAppAdUnitIds),
                );
            } catch (err) {
                showHttpError(err, '加载创建应用数据失败');
                this.createAdUnits = [];
                this.createAppAdUnitIds = new Set();
                this.createGroupsForNames = [];
            } finally {
                this.createLoading = false;
                this.createFormatsLoading = false;
            }
        },
        async openCreateGroup() {
            if (!this.accounts.length) {
                ElMessage.warning('暂无可用账号');
                return;
            }
            this.resetCreateForm();
            this.createOpen = true;
            if (this.accounts.length === 1) {
                this.createAccountId = this.accounts[0].id;
                await this.loadCreateApps();
            }
        },
        buildCreateGroupChange() {
            const adUnitId = this.createForm.adUnitId;
            return {
                displayName: this.createForm.displayName.trim(),
                targeting: {
                    platform: this.createForm.platform,
                    format: this.createForm.format,
                    adUnitIds: [adUnitId],
                },
                state: this.createForm.state || 'ENABLED',
                mediationGroupLines: {
                    '-1': {
                        id: '-1',
                        displayName: 'AdMob Network',
                        adSourceId: ADMOB_NETWORK_SOURCE_ID,
                        cpmMode: 'LIVE',
                        state: 'ENABLED',
                    },
                },
            };
        },
        async dryRunCreateGroup() {
            if (this.createGroupNameTaken) {
                ElMessage.error('名称已存在');
                return;
            }
            if (!this.canSubmitCreate) {
                ElMessage.warning('请填写名称、平台、格式并选择标准广告单元');
                return;
            }
            this.createDryRunning = true;
            this.createError = '';
            this.createDryRunId = '';
            this.createPlanHash = '';
            try {
                const path = `/admob/accounts/${this.createAccountId}/mediation-groups`;
                const result = await admobDryRun('POST', path, this.buildCreateGroupChange());
                const data = result.upstream?.data || {};
                this.createDryRunId = String(data.dry_run_id || '');
                this.createPlanHash = String(data.plan_hash || '');
                if (!this.createDryRunId || !this.createPlanHash) {
                    throw new Error('dry-run 未返回 dry_run_id / plan_hash');
                }
                const warnings = data.warnings || [];
                if (warnings.length) {
                    ElMessage.warning(
                        typeof warnings[0] === 'string'
                            ? warnings[0]
                            : (warnings[0]?.message || 'dry-run 有警告，请确认后 Apply'),
                    );
                } else {
                    ElMessage.success('dry-run 通过，可 Apply');
                }
            } catch (err) {
                this.createError = err?.message || String(err);
                ElMessage.error(this.createError);
            } finally {
                this.createDryRunning = false;
            }
        },
        async applyCreateGroup() {
            if (!this.createDryRunId || !this.createPlanHash) {
                ElMessage.warning('请先执行 dry-run');
                return;
            }
            this.createApplying = true;
            this.createError = '';
            try {
                const path = `/admob/accounts/${this.createAccountId}/mediation-groups`;
                await admobApply('POST', path, this.createDryRunId, this.createPlanHash);
                ElMessage.success('中介组已创建');
                const nextAccountId = this.createAccountId;
                const nextAppId = this.createAppId;
                this.createOpen = false;
                this.accountId = nextAccountId;
                this.appId = nextAppId;
                if (nextAccountId) {
                    localStorage.setItem(ACCOUNT_STORAGE_KEY, nextAccountId);
                } else {
                    localStorage.removeItem(ACCOUNT_STORAGE_KEY);
                }
                if (nextAppId) {
                    localStorage.setItem(APP_STORAGE_KEY, nextAppId);
                } else {
                    localStorage.removeItem(APP_STORAGE_KEY);
                }
                await this.reloadList();
            } catch (err) {
                this.createError = err?.message || String(err);
                ElMessage.error(this.createError);
            } finally {
                this.createApplying = false;
            }
        },
    },
};
</script>

<style scoped>
.list-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
}
.toolbar-left {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
    min-width: 0;
}
.toolbar-right {
    display: flex;
    gap: 8px;
    align-items: center;
}
.filter-group {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    padding: 4px;
    border: 1px solid var(--el-border-color);
    border-radius: 6px;
    background: var(--el-fill-color-blank);
}
.hint-warn {
    color: var(--el-color-warning-dark-2);
}
.format-lock {
    min-width: 64px;
    justify-content: center;
}
.hint-inline {
    margin-left: 8px;
    font-weight: normal;
    font-size: 12px;
    color: var(--el-text-color-secondary);
}
.editor-header-block {
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--el-border-color-lighter);
}
.group-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 12px;
}
.header-actions {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
}
.ad-unit-line {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
}
.ad-unit-line .label {
    color: var(--el-text-color-secondary);
}
.ad-unit-line .value {
    font-weight: 500;
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.ad-source-dropdowns {
    width: 100%;
}
.editor-body {
    padding: 0 8px 24px;
}
.editor-section {
    margin-bottom: 24px;
}
.section-label {
    font-weight: 600;
    margin-bottom: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
}
.section-waterfall {
    border-left: 3px solid var(--el-color-success);
    padding-left: 12px;
}
.targeting-row {
    display: flex;
    gap: 8px;
}
.waterfall-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    padding: 8px;
    margin-bottom: 8px;
    border: 1px dashed var(--el-border-color);
    border-radius: 6px;
    background: var(--el-fill-color-blank);
}
.ab-dialog-body {
    min-height: 120px;
    position: relative;
}
.ab-split {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    align-items: start;
}
.ab-panel {
    min-width: 0;
    padding: 8px;
    border: 1px solid var(--el-border-color-lighter);
    border-radius: 8px;
    background: var(--el-fill-color-blank);
}
.ab-readonly-row {
    border-style: solid;
}
.ab-cell {
    font-size: 13px;
}
.ab-muted {
    color: var(--el-text-color-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 180px;
}
@media (max-width: 960px) {
    .ab-split {
        grid-template-columns: 1fr;
    }
}
.waterfall-row.is-removed {
    opacity: 0.55;
    background: var(--el-fill-color-light);
    border-style: solid;
    border-color: var(--el-border-color-lighter);
    filter: grayscale(0.4);
}
.layer-name-item {
    margin-bottom: 0;
}
.layer-name-item :deep(.el-form-item__error) {
    position: absolute;
    padding-top: 2px;
    white-space: nowrap;
}
.layer-floor-item {
    margin-bottom: 0;
}
.layer-floor-item :deep(.el-form-item__error) {
    position: absolute;
    padding-top: 2px;
    white-space: nowrap;
}
.editor-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}
.confirm-summary {
    margin-bottom: 12px;
    font-size: 14px;
}
.confirm-list {
    margin: 0 0 12px;
    padding-left: 20px;
}
.confirm-list-label {
    font-size: 14px;
}
.confirm-tech {
    margin-top: 8px;
}
.confirm-error-collapse {
    margin-top: 4px;
}
.confirm-error-body {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 12px;
    line-height: 1.4;
}
</style>

<style>
/* Teleported to body — must be unscoped (scoped :deep cannot reach outside component tree) */
.waterfall-editor-dialog.el-dialog {
    height: 90vh;
    margin-top: 5vh;
    display: flex;
    flex-direction: column;
    max-width: 90vw;
}
.waterfall-editor-dialog .el-dialog__header {
    padding-bottom: 0;
}
.waterfall-editor-dialog .el-dialog__body {
    flex: 1;
    overflow: auto;
    padding-top: 8px;
}
.waterfall-editor-dialog .el-dialog__footer {
    flex-shrink: 0;
}
</style>
